import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import JobDescription from "../components/JobDescription";
import CandidateCardsList from '../components/CandidateCardsList';
import { fetchHiringManagers, fetchUsersByRole, GetCandidate, GetDemandPlanning, SaveHiringRequestStatus, UpdateIsConevrtToSource, UpdateHiringRequest, GetCandidateByRequestId, fetchActiveDepartments, fetchDepartments, UpdateFinanceManagerApproved, UpdateHRDirectorApproved, UpdateCEOApproved, HiringRequestStatusHistory, fetchHiringRequests } from '../services/apiService';
import { getDepartmentIdFromToken, getToken, getUserFromToken } from '../services/authService';
import { SendApprovalEmailToHiringManagerAsync, SendSourcingAssignmentEmailAsync, SendApprovalWorkflowEmailAsync } from "../services/sendMailSMTP";
import { toast } from "react-hot-toast";
import Select from 'react-select';
import { UserAddIcon, UsersIcon } from '@heroicons/react/solid';
import LoadingSpinner from '../components/LoadingSpinner';
import QuickAddCandidateModal from "./QuickAddCandidateModal";
import CandidatesModal from "../components/CandidatesModal";

const PRIMARY_COLOR = 'rgb(18, 76, 114)';
const PRIMARY_LIGHT = 'rgba(18, 76, 114, 0.1)';
const SHADOW_COLOR = 'rgba(18, 76, 114, 0.1)';

// ==========================================
// ROLE-BASED PERMISSIONS CONFIGURATION
// ==========================================

const ROLE_PERMISSIONS = {
  'Admin': {
    canViewAllRequests: true,
    canApprove: true,
    canReject: true,
    canHold: true,
    canUnHold: true,
    canConvertToSourcing: true,
    canSendForApproval: true,
    canEdit: true,
    canQuickAddCandidates: true,
    canViewCandidates: true,
    canAssignRecruiter: true,
    canFinanceApprove: false,
    canFinanceReject: false,
  },
  'HR Specialist': {
    canViewAllRequests: true,
    canApprove: false,
    canReject: false,
    canHold: false,
    canUnHold: false,
    canConvertToSourcing: false,
    canSendForApproval: true,
    canEdit: false,
    canQuickAddCandidates: true,
    canViewCandidates: true,
    canAssignRecruiter: false,
    canFinanceApprove: false,
    canFinanceReject: false,
  },
  'HR Manager': {
    canViewAllRequests: true,
    canApprove: false,
    canReject: true,
    canHold: true,
    canUnHold: true,
    canConvertToSourcing: true,
    canSendForApproval: false,
    canEdit: true,
    canQuickAddCandidates: true,
    canViewCandidates: true,
    canAssignRecruiter: true,
    canFinanceApprove: false,
    canFinanceReject: false,
  },
  'HR Manager 2': {
    canViewAllRequests: true,
    canApprove: false,
    canReject: true,
    canHold: true,
    canUnHold: true,
    canConvertToSourcing: true,
    canSendForApproval: false,
    canEdit: false,
    canQuickAddCandidates: true,
    canViewCandidates: true,
    canAssignRecruiter: true,
    canFinanceApprove: false,
    canFinanceReject: false,
  },
  'HR Director': {
    canViewAllRequests: true,
    canApprove: true,
    canReject: true,
    canHold: true,
    canUnHold: true,
    canConvertToSourcing: true,
    canSendForApproval: false,
    canEdit: false,
    canQuickAddCandidates: false,
    canViewCandidates: true,
    canAssignRecruiter: false,
    canFinanceApprove: false,
    canFinanceReject: false,
    canHRDirectorApprove: true,
  },
  'Business Owner': {
    canViewAllRequests: true,
    canApprove: false,
    canReject: false,
    canHold: false,
    canUnHold: false,
    canConvertToSourcing: false,
    canSendForApproval: true,
    canEdit: true,
    canQuickAddCandidates: false,
    canViewCandidates: true,
    canAssignRecruiter: false,
    canFinanceApprove: false,
    canFinanceReject: false,
  },
  'Finance Manager': {
    canViewAllRequests: true,
    canApprove: false,
    canReject: false,
    canHold: false,
    canUnHold: false,
    canConvertToSourcing: false,
    canSendForApproval: false,
    canEdit: false,
    canQuickAddCandidates: false,
    canViewCandidates: true,
    canAssignRecruiter: false,
    canFinanceApprove: true,
    canFinanceReject: true,
  },
  'Chief Finance': {
    canViewAllRequests: true,
    canApprove: false,
    canReject: false,
    canHold: false,
    canUnHold: false,
    canConvertToSourcing: false,
    canSendForApproval: false,
    canEdit: false,
    canQuickAddCandidates: false,
    canViewCandidates: true,
    canAssignRecruiter: false,
    canFinanceApprove: false,
    canFinanceReject: false,
  },
  'CEO': {
    canViewAllRequests: true,
    canApprove: false,
    canReject: false,
    canHold: false,
    canUnHold: false,
    canConvertToSourcing: false,
    canSendForApproval: false,
    canEdit: false,
    canQuickAddCandidates: false,
    canViewCandidates: true,
    canAssignRecruiter: false,
    canFinanceApprove: false,
    canFinanceReject: false,
    canCEOApprove: true,
  },
  'Interview Board': {
    canViewAllRequests: false,
    canApprove: false,
    canReject: false,
    canHold: false,
    canUnHold: false,
    canConvertToSourcing: false,
    canSendForApproval: false,
    canEdit: false,
    canQuickAddCandidates: false,
    canViewCandidates: true,
    canAssignRecruiter: false,
    canFinanceApprove: false,
    canFinanceReject: false,
  },
};

// Default permissions for when no role is matched
const DEFAULT_PERMISSIONS = {
  canViewAllRequests: false,
  canApprove: false,
  canReject: false,
  canHold: false,
  canUnHold: false,
  canConvertToSourcing: false,
  canSendForApproval: false,
  canEdit: false,
  canQuickAddCandidates: false,
  canViewCandidates: true,
  canAssignRecruiter: false,
  canViewRequest: false,
  isDeptRestricted: false,
  roleName: '',
  canFinanceApprove: false,
  canFinanceReject: false,
};

export default function RequestDetails() {
  const navigate = useNavigate();
  const location = useLocation();
  const locationState = location.state;

  // Recover navigation state across page reloads (e.g. mobile-view toggle, direct URL, refresh).
  // Priority: react-router state → sessionStorage cache → API fetch by ?id= URL param.
  const [resolvedState, setResolvedState] = useState(() => {
    if (locationState?.request) {
      try { sessionStorage.setItem('requestDetailsState', JSON.stringify(locationState)); } catch (e) {}
      return locationState;
    }
    try {
      const cached = sessionStorage.getItem('requestDetailsState');
      const parsed = cached ? JSON.parse(cached) : null;
      const urlId = new URLSearchParams(window.location.search).get('id');
      if (parsed?.request && (!urlId || String(parsed.request.request_id) === String(urlId))) return parsed;
    } catch (e) {}
    return null;
  });

  useEffect(() => {
    if (resolvedState?.request) return;
    const urlId = new URLSearchParams(window.location.search).get('id');
    if (!urlId) return;
    (async () => {
      try {
        const [requests, depts, managers, demand] = await Promise.all([
          fetchHiringRequests(),
          fetchActiveDepartments(),
          fetchHiringManagers(),
          GetDemandPlanning(new Date().getFullYear()),
        ]);
        const req = (requests || []).find(r => String(r.request_id) === String(urlId));
        if (!req) return;
        const next = { request: req, departments: depts, hiringManagers: managers, demandPlans: demand };
        try { sessionStorage.setItem('requestDetailsState', JSON.stringify(next)); } catch (e) {}
        setResolvedState(next);
      } catch (e) {
        console.error('Failed to recover request by id:', e);
      }
    })();
  }, [resolvedState]);

  const state = resolvedState;

  const [userRole, setUserRole] = useState(null);
  const [permissions, setPermissions] = useState(DEFAULT_PERMISSIONS);
  const [activeTab, setActiveTab] = useState("details");
  const [candidates, setCandidates] = useState([]);
  const [departmentsList, setDepartmentsList] = useState([]);
  const [demandPlansList, setDemandPlansList] = useState([]);
  const [isPageLoading, setIsPageLoading] = useState(true);
  const [, forceUpdate] = useState(0);
  const [requestDeptName, setRequestDeptName] = useState('');
  const [requestManagerName, setRequestManagerName] = useState('');
  const [approvalFlowUsers, setApprovalFlowUsers] = useState({});
  const [candidatesLoading, setCandidatesLoading] = useState(false);
  const [hasRequest, setHasRequest] = useState(false);
  const [hiringManagers, setHiringManagers] = useState([]);
  const [selectedRecruiter, setSelectedRecruiter] = useState(null);
  const [isConfirmModalVisible, setIsConfirmModalVisible] = useState(false);
  const [isApproveModalVisible, setIsApproveModalVisible] = useState(false);
  const [isRejectModalVisible, setIsRejectModalVisible] = useState(false);
  const [isHoldModalVisible, setIsHoldModalVisible] = useState(false);
  const [isUnHoldModalVisible, setIsUnHoldModalVisible] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [notes, setNotes] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [isEditMode, setIsEditMode] = useState(false);
  const [isSendApproveMode, setIsSendApproveMode] = useState(false);
  const [currentDocument, setCurrentDocument] = useState(null);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [IsAddCandidates, SetIsAddCandidates] = useState(false);
  const [IsQuickAddCandidates, SetIsQuickAddCandidates] = useState(false);
  const [candidateRequests, setCandidateRequests] = useState([]);
  const [isCandidatesModalOpen, setIsCandidatesModalOpen] = useState(false);
  const [hiringManageremail, sethiringManageremail] = useState("");

  // Finance Manager modal states
  const [isFinanceApproveModalVisible, setIsFinanceApproveModalVisible] = useState(false);
  const [isFinanceRejectModalVisible, setIsFinanceRejectModalVisible] = useState(false);
  const [isHRDirectorApproveModalVisible, setIsHRDirectorApproveModalVisible] = useState(false);
  const [isHRDirectorRejectModalVisible, setIsHRDirectorRejectModalVisible] = useState(false);
  const [isCEOApproveModalVisible, setIsCEOApproveModalVisible] = useState(false);
  const [isCEORejectModalVisible, setIsCEORejectModalVisible] = useState(false);

 const [auditTrail, setAuditTrail] = useState([]);
const [auditTrailLoading, setAuditTrailLoading] = useState(false);

  const userRoleData = getToken();
  const userName = getUserFromToken();

  const loadHiringManagers = useCallback(async () => {
    try {
      const managers = await fetchHiringManagers();
      setHiringManagers(managers);
    } catch (err) {
      console.error("Failed to load hiring managers:", err);
    }
  }, []);
const loadAuditTrail = async () => {
  try {
    setAuditTrailLoading(true);
    const history = await HiringRequestStatusHistory(state.request.request_id);
    
    const sorted = [...(history || [])].sort(
      (a, b) => new Date(b.changed_at || b.created_at) - new Date(a.changed_at || a.created_at)
    );
    setAuditTrail(sorted);
  } catch (err) {
    console.error("Failed to load audit trail:", err);
    toast.error("Failed to load audit trail.");
  } finally {
    setAuditTrailLoading(false);
  }
};
  const notifyHiringManager = useCallback(async (request, status, notes = "") => {
    try {
      const hiringManager = hiringManagers.find(
        m => Number(m.user_id) === Number(request.hiring_manager)
      );

      if (!hiringManager?.email) {
        console.warn("Hiring manager email not found");
        return;
      }

      const emailBody = {
        managerEmail: hiringManager.email,
        requestId: request.request_id,
        status: status,
        notes: notes.trim()
      };

      await SendApprovalEmailToHiringManagerAsync(emailBody);
      console.log("Notification email sent to hiring manager");
    } catch (error) {
      console.error("Failed to send notification email to hiring manager:", error);
    }
  }, [hiringManagers]);

  // ==========================================
  // PERMISSIONS
  // ==========================================
  const calculatedPermissions = useMemo(() => {
    if (!userRoleData || !selectedRequest) {
      return DEFAULT_PERMISSIONS;
    }

    const roleName = userRoleData?.role || '';
    const departmentId = getDepartmentIdFromToken();
    const isDeptRestricted = String(userRoleData?.IsDepartmentRestricted || '').toLowerCase() === "true";

    const basePermissions = ROLE_PERMISSIONS[roleName] || {
      canViewAllRequests: false,
      canApprove: false,
      canReject: false,
      canHold: false,
      canUnHold: false,
      canConvertToSourcing: false,
      canSendForApproval: false,
      canEdit: false,
      canQuickAddCandidates: false,
      canViewCandidates: true,
      canAssignRecruiter: false,
      canFinanceApprove: false,
      canFinanceReject: false,
    };

    const canViewRequest = basePermissions.canViewAllRequests ||
      (isDeptRestricted && String(selectedRequest.department_id) === String(departmentId));

    return {
      ...basePermissions,
      canViewRequest,
      isDeptRestricted,
      roleName,
    };
  }, [userRoleData?.role, userRoleData?.IsDepartmentRestricted, selectedRequest?.department_id]);

  useEffect(() => {
    const permissionsString = JSON.stringify(calculatedPermissions);
    const currentPermissionsString = JSON.stringify(permissions);
    if (permissionsString !== currentPermissionsString) {
      setPermissions(calculatedPermissions);
    }
  }, [calculatedPermissions]);

useEffect(() => {
  if (activeTab === "candidates" && hasRequest) {
    loadCandidatesData();
  }
  if (activeTab === "audit" && hasRequest) {
    loadAuditTrail();
  }
}, [activeTab, hasRequest]);

  useEffect(() => {
    const init = async () => {
      try {
        if (state?.request) {
          setHasRequest(true);
          setSelectedRequest(state.request);
          setCurrentDocument(state.request?.Documents || state.currentDocument);
          const [deptData, managersData] = await Promise.all([
            fetchDepartments(),
            fetchHiringManagers(),
          ]);
          if (deptData && deptData.length > 0) {
            setDepartmentsList(deptData);
            const dept = deptData.find(d => Number(d.department_id) === Number(state.request.department_id));
            if (dept) setRequestDeptName(dept.department_name);
          }
          if (managersData && managersData.length > 0) {
            setHiringManagers(managersData);
            const mgr = managersData.find(m => Number(m.user_id) === Number(state.request.hiring_manager));
            if (mgr) setRequestManagerName(`${mgr.first_name || ''} ${mgr.last_name || ''}`.trim());
          }
          // Load audit trail for approval flow display
          if (state.request.request_id) {
            await loadAuditTrail();
          }
          // Load approval flow users for Exceptional/Outsourced
          if (state.request.request_type === "Exceptional" || state.request.request_type === "Outsourced") {
            const [financeUsers, hrDirUsers, ceoUsers] = await Promise.all([
              fetchUsersByRole("Finance Manager"),
              fetchUsersByRole("HR Director"),
              fetchUsersByRole("CEO"),
            ]);
            setApprovalFlowUsers({
              'Finance Manager': financeUsers.map(u => `${u.first_name || ''} ${u.last_name || ''}`.trim()).filter(Boolean),
              'HR Director': hrDirUsers.map(u => `${u.first_name || ''} ${u.last_name || ''}`.trim()).filter(Boolean),
              'CEO': ceoUsers.map(u => `${u.first_name || ''} ${u.last_name || ''}`.trim()).filter(Boolean),
            });
          }
        }
      } finally {
        setIsPageLoading(false);
      }
    };
    init();
  }, [state, loadHiringManagers]);

  const loadCandidatesData = async () => {
    try {
      setCandidatesLoading(true);

      const userRole = getToken();
      const userRoleName = userRole.role;
      const departmentId = getDepartmentIdFromToken();
      const isDeptRestricted = String(userRole.IsDepartmentRestricted).toLowerCase() === "true";

      const deptData = await fetchActiveDepartments();
      if (isDeptRestricted) {
        setDepartmentsList(deptData.filter(dept => String(dept.department_id) === String(departmentId)));
      } else {
        setDepartmentsList(deptData);
      }

      const currentYear = new Date().getFullYear();
      const dplan = await GetDemandPlanning(currentYear);
      if (isDeptRestricted) {
        setDemandPlansList(dplan.filter(plan => String(plan.department_id) === String(departmentId)));
      } else {
        setDemandPlansList(dplan);
      }

      const allCandidates = await GetCandidate();

      let sortedCandidates = [...allCandidates].sort(
        (a, b) => new Date(b.application_date) - new Date(a.application_date)
      );

      if (userRoleName === "Business Owner") {
        sortedCandidates = sortedCandidates.filter(candidate => String(candidate.status) !== "Applied");
      }

      if (isDeptRestricted) {
        sortedCandidates = sortedCandidates.filter(
          candidate => String(candidate.department_id) === String(departmentId)
        );
      }

      const requestCandidates = sortedCandidates.filter(
        candidate => Number(candidate.request_id) === Number(state.request.request_id)
      );

      setCandidates(requestCandidates);

    } catch (err) {
      console.error("Failed to load candidates:", err);
    } finally {
      setCandidatesLoading(false);
    }
  };

  // ==========================================
  // PERMISSION-BASED ACTION CHECKS
  // ==========================================

  // Checks whether this request requires Finance Manager approval
  // Only applicable for Exceptional and Contract request types
  const isFinanceApprovalRequired = () => {
    const req = state?.request;
    if (!req) return false;
    const isExceptionalOrOutsourced =
      req.request_type === "Exceptional" || req.request_type === "Outsourced";
    return isExceptionalOrOutsourced;
  };

  const canApprove = () => {
    if (!state?.request) return false;
    // For Exceptional/Outsourced, HR Manager does NOT approve — goes to Finance Manager directly
    if (state.request.request_type === "Exceptional" || state.request.request_type === "Outsourced") return false;
    return permissions.canApprove && state.request.status === "Submitted";
  };

  // Convert to Sourcing:
  // - For Forecast: just needs Approved status + not already converted
  // - For Exceptional/Contract: additionally needs Finance Manager approval
  const canConvertToSourcing = () => {
    if (!state?.request) return false;
    const baseCondition =
      permissions.canConvertToSourcing &&
      state.request.status === "Approved" &&
      !state.request.isConevrtToSource;

    if (!baseCondition) return false;

    if (isFinanceApprovalRequired()) {
      // All 3 must be approved: Finance → HR Director → CEO
      return (
        state.request.isFinanceManagerApproved === true &&
        state.request.isHRDirectorApproved === true &&
        state.request.isCEOApproved === true
      );
    }

    return true;
  };

  // Approval chain for Exceptional/Outsourced:
  // Step 1: Finance Manager → Step 2: HR Director → Step 3: CEO

  // Finance Manager can approve when: status=Submitted AND not yet approved by Finance
  const canFinanceApprove = () => {
    if (!state?.request) return false;
    return (
      permissions.canFinanceApprove &&
      isFinanceApprovalRequired() &&
      state.request.status === "Submitted" &&
      state.request.isFinanceManagerApproved !== true
    );
  };

  const canFinanceReject = () => {
    if (!state?.request) return false;
    return (
      permissions.canFinanceReject &&
      isFinanceApprovalRequired() &&
      state.request.status === "Submitted" &&
      state.request.isFinanceManagerApproved !== true
    );
  };

  // HR Director can approve when: Finance approved AND HR Director not yet approved
  const canHRDirectorApprove = () => {
    if (!state?.request) return false;
    return (
      permissions.canHRDirectorApprove &&
      isFinanceApprovalRequired() &&
      state.request.status === "Approved" &&
      state.request.isFinanceManagerApproved === true &&
      state.request.isHRDirectorApproved !== true
    );
  };

  // CEO can approve when: HR Director approved AND CEO not yet approved
  const canCEOApprove = () => {
    if (!state?.request) return false;
    return (
      permissions.canCEOApprove &&
      isFinanceApprovalRequired() &&
      state.request.status === "Approved" &&
      state.request.isHRDirectorApproved === true &&
      state.request.isCEOApproved !== true
    );
  };

  const canSendForApproval = () => {
    if (!state?.request) return false;
    return permissions.canSendForApproval && state.request.status === "Draft";
  };

  const canReject = () => {
    if (!state?.request) return false;
    const allowedStatuses = ["Draft", "Submitted"];
    if (!permissions.canReject || !allowedStatuses.includes(state.request.status)) return false;
    // For Exceptional/Outsourced: only show Reject if not in the approval chain (pending Finance/HR Dir/CEO)
    if (isFinanceApprovalRequired() && state.request.status === "Submitted") {
      // Only Finance Manager can reject at this stage (via their own reject button)
      return false;
    }
    return true;
  };

const canHold = () => {
  if (!state?.request) return false;
  const allowedStatuses = ["Approved", "Submitted"];

  // Block Hold during the approval chain for Exceptional/Outsourced
  if (isFinanceApprovalRequired()) {
    const allApproved = state.request.isFinanceManagerApproved === true &&
      state.request.isHRDirectorApproved === true &&
      state.request.isCEOApproved === true;
    if (!allApproved) return false;
  }

  return (
    permissions.canHold &&
    allowedStatuses.includes(state.request.status) &&
    !state.request.isConevrtToSource
  );
};

  const canUnHold = () => {
    if (!state?.request) return false;
    return permissions.canUnHold && state.request.status === "On Hold";
  };

  const canEdit = () => {
    if (!state?.request) return false;
    return permissions.canEdit && state.request.status === "Draft";
  };

  const canQuickAddCandidates = () => {
    if (!state?.request) return false;
    return (
      permissions.canQuickAddCandidates &&
      state.request.isConevrtToSource &&
      state.request.status !== "Offering" &&
      state.request.status !== "Rejected"
    );
  };

  const canViewCandidates = () => {
    if (!permissions.canViewCandidates || !state?.request) return false;
    return state.request.isConevrtToSource === true;
  };

  const canAssignRecruiter = () => {
    return permissions.canAssignRecruiter;
  };

  // ==========================================
  // HANDLER FUNCTIONS
  // ==========================================
  const handleEdit = () => {
    setIsModalVisible(true);
    setIsEditMode(true);
    setIsSendApproveMode(false);
  };

  const handleSendForApprovalClick = () => {
    setIsModalVisible(true);
    setIsSendApproveMode(true);
    setIsEditMode(false);
    setNotes("");
    setErrors({});
  };

  const closeModal = () => {
    setIsModalVisible(false);
    setIsEditMode(false);
    setIsSendApproveMode(false);
    setNotes("");
    setErrors({});
  };

  const handleAddCandidates = () => {
    SetIsAddCandidates(true);
  };

  const handleQuickAddCandidates = () => {
    SetIsQuickAddCandidates(true);
  };

   const handleViewCandidates = async () => {
  try {
    const candidates = await GetCandidateByRequestId(state.request.request_id);

    // Filter "Applied" candidates — only allowed for Admin, HR Specialist, HR Manager
    const rolesAllowedToSeeApplied = ["Admin", "HR Specialist", "HR Manager"];
    const filteredCandidates = rolesAllowedToSeeApplied.includes(userRoleData?.role)
      ? candidates
      : candidates.filter(c => String(c.status) !== "Applied" );

    setCandidateRequests(filteredCandidates);

    const manager = hiringManagers.find(
      m => Number(m.user_id) === Number(state.request.hiring_manager)
    );
    sethiringManageremail(manager?.email || "");

    setIsCandidatesModalOpen(true);
  } catch (error) {
    console.error("Error fetching candidates:", error);
    toast.error("Failed to load candidates");
  }
};

  const closeAddCandidateModal = () => {
    SetIsAddCandidates(false);
  };

  const closeQuickAddCandidateModal = () => {
    SetIsQuickAddCandidates(false);
  };

  const closeCandidatesModal = () => {
    setIsCandidatesModalOpen(false);
  };

  const handleCandidateSubmit = async (candidateData) => {
    try {
      SetIsAddCandidates(false);
      SetIsQuickAddCandidates(false);

      toast.success("Candidate added successfully!");

      if (candidateData && candidateData.candidate_id) {
        setCandidates(prev => {
          const exists = prev.some(c => c.candidate_id === candidateData.candidate_id);
          if (!exists) {
            return [candidateData, ...prev];
          }
          return prev;
        });
      } else {
        if (activeTab === "candidates") {
          await loadCandidatesData();
        }
      }

    } catch (error) {
      console.error("Error handling candidate submission:", error);
      toast.error("Failed to add candidate.");
    }
  };

  const handleRecruiterChange = (selectedOption) => {
    setSelectedRecruiter(selectedOption ? selectedOption.value : null);
    if (errors.recruiter) {
      setErrors(prev => ({ ...prev, recruiter: null }));
    }
  };

  // ==========================================
  // VALIDATION FUNCTIONS
  // ==========================================
  const validateEditForm = () => {
    const newErrors = {};

    if (!selectedRequest.department_id) newErrors.department_id = 'Department is required.';
    if (!selectedRequest.request_type) {
      newErrors.request_type = "Request type is required.";
    }

    if (selectedRequest.request_type === "Forecast") {
      if (!selectedRequest.demand_id) {
        newErrors.demand_id = "Role is required.";
      }
    } else if (selectedRequest.request_type === "Exceptional" || selectedRequest.request_type === "Outsourced") {
      if (!selectedRequest.role_text || selectedRequest.role_text.trim() === "") {
        newErrors.role_text = "Role is required.";
      }
    }

    if (!selectedRequest.hiring_manager) newErrors.hiring_manager = 'Hiring Manager is required.';
    if (!selectedRequest.position) newErrors.position = 'position is required.';
    if (!selectedRequest.reason_for_hire) newErrors.reason_for_hire = 'Reason for Hire is required.';
    if (!selectedRequest.planned_start_date) newErrors.planned_start_date = 'Planned Start Date is required.';
    if (!selectedRequest.status) newErrors.status = 'status is required.';
    if (!selectedRequest.priority) newErrors.priority = 'priority is required.';
    if (!selectedRequest.position_type) newErrors.position_type = 'position Type is required.';

    return newErrors;
  };

  // ==========================================
  // API SUBMISSION FUNCTIONS
  // ==========================================
  const handleEditSave = async () => {
    let validationErrors = {};
    validationErrors = validateEditForm();

    const userName = getUserFromToken();
    selectedRequest.updated_by = userName;

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      setIsSubmitting(false);
      return;
    }

    setErrors({});
    try {
      setIsSubmitting(true);
      const savedRequest = await UpdateHiringRequest(selectedRequest);

      if (Number(savedRequest.status) === 400) {
        toast.error("Hiring request cannot be created as Requested Count exceeds forecasted hires");
      } else if (savedRequest.status === 500) {
        toast.error("Failed to save the hiring request.");
      } else {
        toast.success("Hiring request Updated successfully!");
        state.request = { ...state.request, ...selectedRequest };
        setIsModalVisible(false);
        setIsSubmitting(false);
      }
    } catch (error) {
      console.error('Error saving hiring request:', error);
      toast.error("An error occurred while saving the hiring request.");
    }
  };

  const handleSendForApprovalSave = async () => {
    if (!notes.trim()) {
      setErrors({ notes: "Please enter notes." });
      toast.error("Please enter notes.");
      return;
    }

    try {
      setIsSubmitting(true);
      const bodyReq = {
        Request_id: selectedRequest.request_id,
        old_status: selectedRequest.status,
        new_status: "Submitted",
        logDescription: notes.trim(),
        change_by: userName,
      };

      const savedRequest = await SaveHiringRequestStatus(bodyReq);

      if (savedRequest) {
        toast.success("Request sent for approval successfully!");
        selectedRequest.status = "Submitted";
        state.request.status = "Submitted";

        // Route email based on request type
        const isExceptionalOrOutsourced = selectedRequest.request_type === "Exceptional" || selectedRequest.request_type === "Outsourced";
        if (isExceptionalOrOutsourced) {
          // Exceptional/Outsourced → Finance Manager
          await sendWorkflowEmail("Finance Manager", "Submitted", "Business Owner", "Pending Finance Approval", notes.trim());
        } else {
          // Forecast → HR Manager
          await sendWorkflowEmail("HR Director", "Submitted", "Business Owner", "Pending HR Director Approval", notes.trim());
        }
        closeModal();
      } else {
        toast.error("Failed to send request for approval.");
      }
    } catch (error) {
      console.error("Error sending for approval:", error);
      toast.error("An error occurred while sending for approval.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleApprove = async () => {
    if (!notes.trim()) {
      setErrors({ notes: "Please enter notes." });
      toast.error("Please enter notes.");
      return;
    }

    try {
      setIsSubmitting(true);
      const bodyReq = {
        Request_id: state.request.request_id,
        old_status: state.request.status,
        new_status: "Approved",
        logDescription: notes.trim(),
        change_by: userName,
      };

      const savedRequest = await SaveHiringRequestStatus(bodyReq);

      if (savedRequest) {
        toast.success("Request approved successfully!");
        state.request.status = "Approved";

        await notifyHiringManager(state.request, "Approved", notes);

        setIsApproveModalVisible(false);
        setNotes("");
        setErrors({});
        forceUpdate(n => n + 1);
      } else {
        toast.error("Failed to approve request.");
      }
    } catch (error) {
      console.error("Error approving request:", error);
      toast.error("An error occurred while approving the request.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Finance Manager Approve
  // Helper: send approval workflow email with CC to business owner
  // Send approval workflow email — backend finds recipients by role from DB
  const sendWorkflowEmail = async (recipientRole, action, actorRole, nextStep, emailNotes) => {
    try {
      await SendApprovalWorkflowEmailAsync({
        recipientRole,
        companyCode: selectedRequestFromState?.companyCode || '',
        hiringManagerUserId: Number(selectedRequestFromState?.hiring_manager) || null,
        requestId: selectedRequestFromState?.request_id,
        referenceNumber: selectedRequestFromState?.referenceNumber || '',
        action, actorName: userName, actorRole, nextStep,
        notes: emailNotes || '',
        department: requestDeptName || requestDepartment?.department_name || '',
        position: selectedRequestFromState?.position || '',
        requestType: selectedRequestFromState?.request_type || '',
      });
    } catch (err) {
      console.error("Failed to send workflow email:", err);
    }
  };

  const handleFinanceApprove = async () => {
    if (!notes.trim()) {
      setErrors({ notes: "Please enter notes." });
      toast.error("Please enter notes.");
      return;
    }

    try {
      setIsSubmitting(true);
      const bodyReq = {
        Request_id: state.request.request_id,
        old_status: state.request.status,
        new_status: "Approved",
        logDescription: notes.trim(),
        change_by: userName,
        isFinanceManagerApproved: true,
      };

      const savedRequest = await UpdateFinanceManagerApproved(bodyReq);

      if (savedRequest) {
        toast.success("Finance approval granted successfully!");
        state.request.isFinanceManagerApproved = true;
        state.request.status = "Approved";
        await sendWorkflowEmail("HR Director", "Finance Approved", "Finance Manager", "Pending HR Director Approval", notes.trim());

        setIsFinanceApproveModalVisible(false);
        setNotes("");
        setErrors({});
        forceUpdate(n => n + 1);
      } else {
        toast.error("Failed to record finance approval.");
      }
    } catch (error) {
      console.error("Error recording finance approval:", error);
      toast.error("An error occurred while recording finance approval.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleHRDirectorApprove = async () => {
    if (!notes.trim()) {
      setErrors({ notes: "Please enter notes." });
      toast.error("Please enter notes.");
      return;
    }
    try {
      setIsSubmitting(true);
      await UpdateHRDirectorApproved({
        Request_id: state.request.request_id,
        old_status: state.request.status,
        new_status: state.request.status,
        logDescription: notes.trim(),
        change_by: userName,
      });
      toast.success("HR Director approval recorded.");
      state.request.isHRDirectorApproved = true;
      await sendWorkflowEmail("CEO", "HR Director Approved", "HR Director", "Pending CEO Approval", notes.trim());
      setIsHRDirectorApproveModalVisible(false);
      setNotes("");
      setErrors({});
      forceUpdate(n => n + 1);
    } catch (error) {
      console.error("Error recording HR Director approval:", error);
      toast.error("Failed to record HR Director approval.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCEOApprove = async () => {
    if (!notes.trim()) {
      setErrors({ notes: "Please enter notes." });
      toast.error("Please enter notes.");
      return;
    }
    try {
      setIsSubmitting(true);
      await UpdateCEOApproved({
        Request_id: state.request.request_id,
        old_status: state.request.status,
        new_status: state.request.status,
        logDescription: notes.trim(),
        change_by: userName,
      });
      toast.success("CEO approval recorded.");
      state.request.isCEOApproved = true;
      await sendWorkflowEmail("HR Director", "CEO Approved", "CEO", "Ready for Convert to Sourcing", notes.trim());
      setIsCEOApproveModalVisible(false);
      setNotes("");
      setErrors({});
      forceUpdate(n => n + 1);
    } catch (error) {
      console.error("Error recording CEO approval:", error);
      toast.error("Failed to record CEO approval.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleHRDirectorReject = async () => {
    if (!notes.trim()) { setErrors({ notes: "Please enter notes." }); toast.error("Please enter notes."); return; }
    try {
      setIsSubmitting(true);
      await SaveHiringRequestStatus({
        Request_id: state.request.request_id,
        old_status: state.request.status,
        new_status: "Rejected",
        logDescription: `Rejected by HR Director: ${notes.trim()}`,
        change_by: userName,
      });
      toast.success("Request rejected by HR Director.");
      state.request.status = "Rejected";
      {
        await SendApprovalWorkflowEmailAsync({
          recipientRole: '',
          companyCode: selectedRequestFromState?.companyCode || '',
          hiringManagerUserId: Number(selectedRequestFromState?.hiring_manager) || null,
          requestId: selectedRequestFromState?.request_id, referenceNumber: selectedRequestFromState?.referenceNumber || '',
          action: "Rejected", actorName: userName, actorRole: "HR Director", nextStep: "Request Rejected",
          notes: notes.trim(), department: requestDeptName || '', position: selectedRequestFromState?.position || '', requestType: selectedRequestFromState?.request_type || '',
        });
      }
      setIsHRDirectorRejectModalVisible(false);
      setNotes(""); setErrors({});
      forceUpdate(n => n + 1);
    } catch (error) {
      console.error("Error rejecting request (HR Director):", error);
      toast.error("Failed to reject request.");
    } finally { setIsSubmitting(false); }
  };

  const handleCEOReject = async () => {
    if (!notes.trim()) { setErrors({ notes: "Please enter notes." }); toast.error("Please enter notes."); return; }
    try {
      setIsSubmitting(true);
      await SaveHiringRequestStatus({
        Request_id: state.request.request_id,
        old_status: state.request.status,
        new_status: "Rejected",
        logDescription: `Rejected by CEO: ${notes.trim()}`,
        change_by: userName,
      });
      toast.success("Request rejected by CEO.");
      state.request.status = "Rejected";
      // Email business owner about rejection
      const boCEO = (hiringManagers.length > 0 ? hiringManagers : hiringManagersFromState || []).find(m => Number(m.user_id) === Number(selectedRequestFromState?.hiring_manager));
      if (boCEO?.email) {
        await SendApprovalWorkflowEmailAsync({
          toEmails: boCEO.email, ccEmail: '',
          requestId: selectedRequestFromState?.request_id, referenceNumber: selectedRequestFromState?.referenceNumber || '',
          action: "Rejected", actorName: userName, actorRole: "CEO", nextStep: "Request Rejected",
          notes: notes.trim(), department: requestDeptName || '', position: selectedRequestFromState?.position || '', requestType: selectedRequestFromState?.request_type || '',
        });
      }
      setIsCEORejectModalVisible(false);
      setNotes(""); setErrors({});
      forceUpdate(n => n + 1);
    } catch (error) {
      console.error("Error rejecting request (CEO):", error);
      toast.error("Failed to reject request.");
    } finally { setIsSubmitting(false); }
  };

  // Finance Manager Reject
  const handleFinanceReject = async () => {
    if (!notes.trim()) {
      setErrors({ notes: "Please enter notes." });
      toast.error("Please enter notes.");
      return;
    }

    try {
      setIsSubmitting(true);
      const bodyReq = {
        Request_id: state.request.request_id,
        old_status: state.request.status,
        new_status: "Rejected",
        logDescription: notes.trim(),
        change_by: userName,
        isFinanceManagerApproved: false,
      };

      const savedRequest = await SaveHiringRequestStatus(bodyReq);

      if (savedRequest) {
        toast.success("Request rejected by Finance Manager.");
        state.request.status = "Rejected";
        state.request.isFinanceManagerApproved = false;

        await notifyHiringManager(state.request, "Rejected by Finance Manager", notes);

        setIsFinanceRejectModalVisible(false);
        setNotes("");
        setErrors({});
        forceUpdate(n => n + 1);
      } else {
        toast.error("Failed to reject request.");
      }
    } catch (error) {
      console.error("Error rejecting request (finance):", error);
      toast.error("An error occurred while rejecting the request.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleConvertToSourcing = async () => {
    if (!selectedRecruiter) {
      setErrors({ recruiter: "Please select a recruiter." });
      toast.error("Please select a recruiter.");
      return;
    }

    try {
      setIsSubmitting(true);
      const bodyReq = {
        Request_id: state.request.request_id,
        old_status: state.request.status,
        new_status: state.request.status,
        logDescription: "Convert to sourcing",
        change_by: userName,
        recruiter_id: selectedRecruiter,
      };

      const savedRequest = await UpdateIsConevrtToSource(bodyReq);

      if (savedRequest) {
        toast.success("Successfully converted to Sourcing!");
        state.request.isConevrtToSource = true;
        setIsConfirmModalVisible(false);
        setSelectedRecruiter(null);
        setErrors({});

        const recruiter = hiringManagers.find(m => m.user_id === selectedRecruiter);
        if (recruiter) {
          const department = departmentsList.find(
            dept => Number(dept.department_id) === Number(state.request.department_id)
          )?.department_name || "";

          const role = state.request.request_type === "Forecast"
            ? demandPlansList.find(plan => Number(plan.demand_id) === Number(state.request.demand_id))?.role
            : state.request.role_text;

          const emailRequest = {
            RecipientEmail: recruiter.email,
            RecipientName: `${recruiter.first_name} ${recruiter.last_name}`,
            Role: `${role} (${state.request.position})`,
            Department: department,
            RequestId: state.request.request_id
          };

          await SendSourcingAssignmentEmailAsync(emailRequest);
        }
      } else {
        toast.error("Failed to convert to sourcing.");
      }
    } catch (error) {
      console.error("Error converting to sourcing:", error);
      toast.error("An error occurred while converting to sourcing.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReject = async () => {
    if (!notes.trim()) {
      setErrors({ notes: "Please enter notes." });
      toast.error("Please enter notes.");
      return;
    }

    try {
      setIsSubmitting(true);
      const bodyReq = {
        Request_id: state.request.request_id,
        old_status: state.request.status,
        new_status: "Rejected",
        logDescription: notes.trim(),
        change_by: userName,
        demand_id: state.request.demand_id
      };

      const savedRequest = await SaveHiringRequestStatus(bodyReq);

      if (savedRequest) {
        toast.success("Request rejected successfully!");
        state.request.status = "Rejected";

        await notifyHiringManager(state.request, "Rejected", notes);

        setIsRejectModalVisible(false);
        setNotes("");
        setErrors({});
        forceUpdate(n => n + 1);
      } else {
        toast.error("Failed to reject request.");
      }
    } catch (error) {
      console.error("Error rejecting request:", error);
      toast.error("An error occurred while rejecting the request.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleHold = async () => {
    if (!notes.trim()) {
      setErrors({ notes: "Please enter notes." });
      toast.error("Please enter notes.");
      return;
    }

    try {
      setIsSubmitting(true);
      const bodyReq = {
        Request_id: state.request.request_id,
        old_status: state.request.status,
        new_status: "On Hold",
        logDescription: notes.trim(),
        change_by: userName,
      };

      const savedRequest = await SaveHiringRequestStatus(bodyReq);

      if (savedRequest) {
        toast.success("Request put on hold successfully!");
        state.request.status = "On Hold";

        await notifyHiringManager(state.request, "On Hold", notes);

        setIsHoldModalVisible(false);
        setNotes("");
        setErrors({});
        forceUpdate(n => n + 1);
      } else {
        toast.error("Failed to put request on hold.");
      }
    } catch (error) {
      console.error("Error putting request on hold:", error);
      toast.error("An error occurred while putting request on hold.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUnHold = async () => {
    if (!notes.trim()) {
      setErrors({ notes: "Please enter notes." });
      toast.error("Please enter notes.");
      return;
    }

    try {
      setIsSubmitting(true);
      const bodyReq = {
        Request_id: state.request.request_id,
        old_status: "On Hold",
        new_status: "Submitted",
        logDescription: notes.trim(),
        change_by: userName,
      };

      const savedRequest = await SaveHiringRequestStatus(bodyReq);

      if (savedRequest) {
        toast.success("Request removed from hold successfully!");
        state.request.status = "Submitted";

        await notifyHiringManager(state.request, "Submitted", notes);

        setIsUnHoldModalVisible(false);
        setNotes("");
        setErrors({});
        forceUpdate(n => n + 1);
      } else {
        toast.error("Failed to remove request from hold.");
      }
    } catch (error) {
      console.error("Error removing request from hold:", error);
      toast.error("An error occurred while removing request from hold.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isPageLoading) return <LoadingSpinner label="Loading Request Details" />;

  if (!state?.request) {
    return (
      <div className="flex flex-col items-center justify-center h-screen text-center">
        <h2 className="text-xl font-semibold text-gray-700">No request details available</h2>
        <button
          onClick={() => navigate(-1)}
          className="mt-4 bg-primary-800 text-white px-4 py-2 rounded-md hover:bg-primary-900 transition"
        >
          Go Back
        </button>
      </div>
    );
  }

  const {
    request: selectedRequestFromState,
    demandPlans,
    departments,
    hiringManagers: hiringManagersFromState,
  } = state;

  const requestDepartment = departmentsList.find(
    dept => Number(dept.department_id) === Number(selectedRequestFromState.department_id)
  ) || departments?.find(
    dept => Number(dept.department_id) === Number(selectedRequestFromState.department_id)
  );

  const filteredDepartments = requestDepartment ? [requestDepartment] : [];

  const filteredDemandPlans = demandPlansList.filter(
    plan => Number(plan.department_id) === Number(selectedRequestFromState.department_id)
  );

  const role =
    selectedRequestFromState.request_type === "Forecast"
      ? demandPlans?.find(d => Number(d.demand_id) === Number(selectedRequestFromState.demand_id))?.role || "N/A"
      : selectedRequestFromState.role_text || "N/A";

  const manager = (() => {
    const m = hiringManagers.find(m => Number(m.user_id) === Number(selectedRequestFromState.hiring_manager))
      || hiringManagersFromState?.find(m => Number(m.user_id) === Number(selectedRequestFromState.hiring_manager));
    return m ? `${m.first_name || ''} ${m.last_name || ''}`.trim() : requestManagerName || 'N/A';
  })();

  const statusColor = selectedRequestFromState.status === "Draft" ? "bg-yellow-500" :
    selectedRequestFromState.status === "Submitted" ? "bg-blue-500" :
      selectedRequestFromState.status === "Approved" ? "bg-green-500" :
        selectedRequestFromState.status === "Rejected" ? "bg-red-500" :
          selectedRequestFromState.status === "On Hold" ? "bg-yellow-500" : "bg-gray-500";

  // ==========================================
  // PERMISSION-BASED ACTION BUTTONS
  // ==========================================
  const ActionButtons = () => (
    <div className="flex flex-wrap gap-2 mt-4">
      <button
        onClick={() => navigate(-1)}
        className="bg-primary-100 text-primary-800 px-3 py-1.5 rounded-md hover:bg-primary-200 transition flex items-center gap-2"
      >
        ← Back
      </button>

      {canEdit() && (
        <button
          onClick={handleEdit}
          className="bg-primary-600 text-white px-4 py-2 rounded-md hover:bg-primary-700 transition"
        >
          Edit Request
        </button>
      )}

      {canSendForApproval() && (
        <button
          onClick={handleSendForApprovalClick}
          className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition"
        >
          Send for Approval
        </button>
      )}

      {canApprove() && (
        <button
          onClick={() => setIsApproveModalVisible(true)}
          className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 transition"
        >
          Approve
        </button>
      )}

      {/* Finance Manager Approve Button */}
      {canFinanceApprove() && (
        <button
          onClick={() => { setNotes(""); setErrors({}); setIsFinanceApproveModalVisible(true); }}
          className="bg-emerald-600 text-white px-4 py-2 rounded-md hover:bg-emerald-700 transition flex items-center gap-2"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          Finance Approve
        </button>
      )}

      {/* Finance Manager Reject Button */}
      {canFinanceReject() && (
        <button
          onClick={() => { setNotes(""); setErrors({}); setIsFinanceRejectModalVisible(true); }}
          className="bg-rose-600 text-white px-4 py-2 rounded-md hover:bg-rose-700 transition flex items-center gap-2"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          Finance Reject
        </button>
      )}

      {/* HR Director Approve Button */}
      {canHRDirectorApprove() && (
        <button
          onClick={() => { setNotes(""); setErrors({}); setIsHRDirectorApproveModalVisible(true); }}
          className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition flex items-center gap-2"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          HR Director Approve
        </button>
      )}
      {canHRDirectorApprove() && (
        <button
          onClick={() => { setNotes(""); setErrors({}); setIsHRDirectorRejectModalVisible(true); }}
          className="bg-rose-600 text-white px-4 py-2 rounded-md hover:bg-rose-700 transition flex items-center gap-2"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          HR Director Reject
        </button>
      )}

      {/* CEO Approve Button */}
      {canCEOApprove() && (
        <button
          onClick={() => { setNotes(""); setErrors({}); setIsCEOApproveModalVisible(true); }}
          className="bg-indigo-600 text-white px-4 py-2 rounded-md hover:bg-indigo-700 transition flex items-center gap-2"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          CEO Approve
        </button>
      )}
      {canCEOApprove() && (
        <button
          onClick={() => { setNotes(""); setErrors({}); setIsCEORejectModalVisible(true); }}
          className="bg-rose-600 text-white px-4 py-2 rounded-md hover:bg-rose-700 transition flex items-center gap-2"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          CEO Reject
        </button>
      )}

      {canConvertToSourcing() && (
        <button
          onClick={() => setIsConfirmModalVisible(true)}
          className="bg-purple-600 text-white px-4 py-2 rounded-md hover:bg-purple-700 transition"
        >
          Convert to Sourcing
        </button>
      )}

      {canReject() && (
        <button
          onClick={() => setIsRejectModalVisible(true)}
          className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 transition"
        >
          Reject
        </button>
      )}

      {canHold() && (
        <button
          onClick={() => setIsHoldModalVisible(true)}
          className="bg-yellow-600 text-white px-4 py-2 rounded-md hover:bg-yellow-700 transition"
        >
          Hold
        </button>
      )}

      {canUnHold() && (
        <button
          onClick={() => setIsUnHoldModalVisible(true)}
          className="bg-orange-600 text-white px-4 py-2 rounded-md hover:bg-orange-700 transition"
        >
          Unhold
        </button>
      )}

      {canQuickAddCandidates() && (
        <button
          onClick={handleQuickAddCandidates}
          className="bg-cyan-600 text-white px-4 py-2 rounded-md hover:bg-cyan-700 transition flex items-center gap-2"
        >
          <UserAddIcon className="w-4 h-4" />
          Quick Add Candidates
        </button>
      )}

      {canViewCandidates() && (
        <button
          onClick={handleViewCandidates}
          className="bg-indigo-600 text-white px-4 py-2 rounded-md hover:bg-indigo-700 transition flex items-center gap-2"
        >
          <UsersIcon className="w-4 h-4" />
          View Candidates
        </button>
      )}
    </div>
  );

  const QuickAddCandidateModalWrapper = () => (
    IsQuickAddCandidates && (
      <QuickAddCandidateModal
        isOpen={IsQuickAddCandidates}
        onClose={closeQuickAddCandidateModal}
        request_id={state?.request?.request_id}
        companyCode={state?.request?.companyCode}
        onSubmit={handleCandidateSubmit}
      />
    )
  );

  const CandidatesModalWrapper = () => (
    isCandidatesModalOpen && (
      <CandidatesModal
        isOpen={isCandidatesModalOpen}
        onClose={closeCandidatesModal}
        candidates={candidateRequests}
        hiringManageremail={hiringManageremail}
        hiringRequests={state?.request}
      />
    )
  );

  // ==========================================
  // RENDER COMPONENT
  // ==========================================
  return (
    <div className='
          bg-white
          mx-4
          pt-4 px-4 rounded-t-[32px]
          md:pt-8 md:px-[calc(5%-1rem)] md:rounded-t-[40px]
          lg:px-[calc(6%-1rem)]
          xl:h-[100%] xl:pt-24 xl:px-[calc(2.5%-1rem)] xl:rounded-t-[48px]
          2xl:px-[calc(8%-1rem)]
          3xl:px-[calc(8%-1rem)]
          4xl:px-[calc(10%-1rem)]
    '>
      <div className='relative xl:-top-[180px]' >
        <div className="grid grid-cols-1 gap-6 pt-2">
          <div className="bg-white rounded-2xl p-6 shadow-lg" style={{
            border: `1px solid ${PRIMARY_LIGHT}`,
            boxShadow: `0 5px 30px ${SHADOW_COLOR}`
          }}>

            {/* Header with ActionButtons */}
            <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center mb-6 gap-4 w-full">
              <div className="flex items-start sm:items-center gap-3 min-w-0 w-full lg:w-auto">
                <button onClick={() => navigate(-1)} className="p-2 rounded-lg hover:bg-primary-800/10 transition flex-shrink-0" style={{ backgroundColor: PRIMARY_LIGHT }}>
                  <svg className="w-6 h-6" style={{ color: PRIMARY_COLOR }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                <div className="min-w-0 flex-1">
                  <h2 className="text-lg sm:text-xl lg:text-2xl font-bold break-words" style={{ color: PRIMARY_COLOR }}>Hiring Request Details</h2>
                  <div className="flex items-center gap-2 mt-1">
                    <div className={`w-3 h-3 rounded-full ${statusColor} flex-shrink-0`}></div>
                    <span className="font-medium text-sm sm:text-base" style={{ color: PRIMARY_COLOR }}>{selectedRequestFromState.status}</span>
                  </div>
                </div>
              </div>

              {/* Permission-based ActionButtons placed on the right */}
              <ActionButtons />
            </div>

            {/* Tabs */}
            {permissions.canViewRequest && (
              <>
              {/* Approval Flow - single row before tabs. Hidden for requests created before production cutoff (2026-04-20). Safari-safe date parsing: replace space with 'T' for ISO compatibility. */}
              {isFinanceApprovalRequired() && selectedRequestFromState?.date_created && !isNaN(new Date(String(selectedRequestFromState.date_created).replace(' ', 'T')).getTime()) && new Date(String(selectedRequestFromState.date_created).replace(' ', 'T')).getTime() >= new Date('2026-04-20T00:00:00').getTime() && (
                <div className="flex items-center gap-2 mt-4 mb-2 p-3 rounded-lg overflow-x-auto" style={{ backgroundColor: 'rgba(18, 76, 114, 0.03)', border: '1px solid rgba(18, 76, 114, 0.08)' }}>
                  {[
                    { role: 'Finance Manager', label: 'Finance', approved: !!selectedRequestFromState.isFinanceManagerApproved, current: !selectedRequestFromState.isFinanceManagerApproved },
                    { role: 'HR Director', label: 'HR Director', approved: !!selectedRequestFromState.isHRDirectorApproved, current: !!selectedRequestFromState.isFinanceManagerApproved && !selectedRequestFromState.isHRDirectorApproved },
                    { role: 'CEO', label: 'CEO', approved: !!selectedRequestFromState.isCEOApproved, current: !!selectedRequestFromState.isHRDirectorApproved && !selectedRequestFromState.isCEOApproved },
                  ].map((step, idx) => {
                    const approver = step.approved
                      ? (auditTrail || []).find(a =>
                          (a.change_by && a.change_by.toLowerCase().includes(step.role.toLowerCase())) ||
                          (a.logDescription && a.logDescription.toLowerCase().includes(step.role.toLowerCase()))
                        )
                      : null;
                    const pendingUsers = !step.approved ? (approvalFlowUsers[step.role] || []) : [];
                    const color = step.approved ? '#059669' : step.current ? '#d97706' : '#9ca3af';
                    return (
                      <React.Fragment key={idx}>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <div className="flex items-center justify-center w-7 h-7 rounded-full text-xs font-bold" style={{ backgroundColor: color, color: '#fff' }}>
                            {step.approved ? '✓' : idx + 1}
                          </div>
                          <div>
                            <div className="text-xs font-semibold whitespace-nowrap" style={{ color }}>{step.label}</div>
                            <div className="text-[10px] whitespace-nowrap" style={{ color: step.approved ? '#059669' : '#6b7280' }}>
                              {step.approved
                                ? `${approver?.change_by || 'Approved'}${approver?.change_date ? ' · ' + new Date(approver.change_date).toLocaleDateString() : ''}`
                                : step.current
                                  ? `⏳ ${pendingUsers.length > 0 ? pendingUsers.join(', ') : 'Pending'}`
                                  : pendingUsers.length > 0 ? pendingUsers.join(', ') : 'Waiting'}
                            </div>
                          </div>
                        </div>
                        {idx < 2 && (
                          <div className="flex-shrink-0 w-8 h-0.5 rounded" style={{ backgroundColor: step.approved ? '#059669' : '#e5e7eb' }} />
                        )}
                      </React.Fragment>
                    );
                  })}
                </div>
              )}

              <div className="border-b border-gray-200 mb-4 mt-6">
                <nav className="flex gap-4 -mb-px flex-wrap">
                  {["details", "jd", ...(canViewCandidates() ? ["candidates"] : []), "audit"].map((tab) => (
                    <button
                      key={tab}
                      className={`px-4 py-2 font-medium text-sm rounded-t-lg transition-colors duration-300 ease-in-out ${activeTab === tab
                          ? "text-white bg-primary-600"
                          : "text-primary-800 bg-primary-600/30 hover:bg-gray-300"
                        }`}
                      onClick={() => setActiveTab(tab)}
                    >
                    {tab === "details" ? "Details" :
  tab === "jd" ? "Job Description" :
  tab === "candidates" ? "Candidates" :
    "Audit Trail"}
                    </button>
                  ))}
                </nav>
              </div>
              </>
            )}

            {/* Details Tab */}
            {activeTab === "details" && permissions.canViewRequest && (
              
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                {[
                  { label: "Reference Number", value: selectedRequestFromState.referenceNumber },
                  { label: "Request Type", value: selectedRequestFromState.request_type },
                  {
                    label: "Department",
                    value: requestDepartment?.department_name || "N/A",
                  },
                  { label: "Role", value: role },
                  { label: "Position", value: selectedRequestFromState.position },
                  { label: "Hiring Manager", value: manager },
                  {
                    label: "Planned Start Date",
                    value: selectedRequestFromState.planned_start_date
                      ? new Date(selectedRequestFromState.planned_start_date).toLocaleDateString()
                      : "N/A",
                  },
                  { label: "Priority", value: selectedRequestFromState.priority },
                  { label: "Hiring Type", value: selectedRequestFromState.hiring_Type },
                  { label: "Reason for Hire", value: selectedRequestFromState.reason_for_hire },
                  ...(selectedRequestFromState.request_type === "Exceptional" || selectedRequestFromState.request_type === "Outsourced"
                    ? [{ label: "Preferred Requirement", value: selectedRequestFromState.preferred_requirment }]
                    : []),
              
                ].map((item, idx) => (
                  
                  <div
                    key={idx}
                    className="flex flex-col bg-white p-4 shadow-sm hover:shadow-md transition"
                  >
                    <span className="text-gray-500 text-sm">{item.label}</span>
                    <span className="font-bold text-gray-700 break-words whitespace-normal">{item.value}</span>
                  </div>
                ))}
              </div>
            )}

            {/* Job Description Tab */}
            {activeTab === "jd" && permissions.canViewRequest && (
              <div className="pt-4">
                <JobDescription
                  activeTab={activeTab}
                  selectedRequest={
                    (selectedRequestFromState.request_type === "Exceptional" || selectedRequestFromState.request_type === "Outsourced")
                      ? selectedRequestFromState
                      : {
                        ...demandPlans.find(
                          (d) => Number(d.demand_id) === Number(selectedRequestFromState.demand_id)
                        ),
                        request_id: selectedRequestFromState?.demand_id,
                      }
                  }
                  isEditMode={false}
                  currentDocument={currentDocument || selectedRequestFromState?.Documents}
                />
              </div>
            )}

            {/* Candidates Tab */}
      {/* Candidates Tab */}
{activeTab === "candidates" && permissions.canViewRequest && (
  <div className="pt-4">
    {!canViewCandidates() ? (
      <div className="text-center py-8">
        <p className="text-gray-500">You don't have permission to view candidates.</p>
      </div>
    ) : candidatesLoading ? (
      <div className="flex justify-center items-center h-32">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-800"></div>
      </div>
    ) : candidates.length > 0 ? (
      <CandidateCardsList
        candidates={candidates}
        departments={filteredDepartments}
        demandPlans={filteredDemandPlans}
        isEmbedded={true}
        showStatusFilter={false}
      />
    ) : (
      <div className="text-center py-8">
        <p className="text-gray-500">No candidates found for this hiring request.</p>
      </div>
    )}
  </div>
)}
{/* Audit Trail Tab */}
{activeTab === "audit" && permissions.canViewRequest && (
  <div className="pt-4">
    {auditTrailLoading ? (
      <div className="flex justify-center items-center h-32">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-800"></div>
      </div>
    ) : auditTrail.length === 0 ? (
      <div className="text-center py-8">
        <p className="text-gray-500">No audit history found for this request.</p>
      </div>
    ) : (
      <div className="relative">
        {/* Timeline line */}
        <div
          className="absolute left-5 top-0 bottom-0 w-0.5"
          style={{ backgroundColor: 'rgba(18, 76, 114, 0.15)' }}
        />
        <div className="space-y-4">
          {auditTrail.map((entry, idx) => {
            const statusColors = {
              Draft:      { bg: 'rgba(234, 179, 8, 0.12)',  text: '#a16207',  dot: '#eab308' },
              Submitted:  { bg: 'rgba(59, 130, 246, 0.12)', text: '#1d4ed8',  dot: '#3b82f6' },
              Approved:   { bg: 'rgba(34, 197, 94, 0.12)',  text: '#15803d',  dot: '#22c55e' },
              Rejected:   { bg: 'rgba(239, 68, 68, 0.12)',  text: '#b91c1c',  dot: '#ef4444' },
              'On Hold':  { bg: 'rgba(245, 158, 11, 0.12)', text: '#92400e',  dot: '#f59e0b' },
            };
            const colors = statusColors[entry.new_status] || { bg: 'rgba(18,76,114,0.08)', text: 'rgb(18,76,114)', dot: 'rgb(18,76,114)' };
            const changedAt = entry.change_date || entry.created_at;
const parseDate = (str) => {
  if (!str) return null;
  // If no timezone info, treat as UTC
  return str.endsWith('Z') || str.includes('+') ? new Date(str) : new Date(str + 'Z');
};
const dateStr = changedAt ? parseDate(changedAt).toLocaleString('en-US', { timeZone: 'Asia/Riyadh' }) : 'N/A';

            return (
              <div key={idx} className="relative flex gap-4 pl-3">
                {/* Dot on timeline */}
                <div
                  className="relative z-10 flex-shrink-0 w-5 h-5 rounded-full border-2 border-white mt-3 shadow-sm"
                  style={{ backgroundColor: colors.dot }}
                />
                {/* Card */}
                <div
                  className="flex-1 rounded-xl p-4 shadow-sm hover:shadow-md transition-shadow duration-200"
                  style={{
                    border: `1px solid rgba(18, 76, 114, 0.1)`,
                    backgroundColor: 'white',
                  }}
                >
                  <div className="flex flex-wrap items-start justify-between gap-2 mb-2">
                    <div className="flex items-center gap-2 flex-wrap">
                      {/* Status transition badges */}
                      {entry.old_status && (
                        <>
                          <span
                            className="text-xs font-medium px-2.5 py-1 rounded-full"
                            style={{
                              backgroundColor: (statusColors[entry.old_status] || { bg: 'rgba(18,76,114,0.08)' }).bg,
                              color: (statusColors[entry.old_status] || { text: 'rgb(18,76,114)' }).text,
                            }}
                          >
                            {entry.old_status}
                          </span>
                          <svg className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7l5 5m0 0l-5 5m5-5H6" />
                          </svg>
                        </>
                      )}
                      <span
                        className="text-xs font-semibold px-2.5 py-1 rounded-full"
                        style={{ backgroundColor: colors.bg, color: colors.text }}
                      >
                        {entry.new_status}
                      </span>
                    </div>
                    {/* Timestamp */}
                    <span className="text-xs text-gray-400 whitespace-nowrap">{dateStr}</span>
                  </div>

                  {/* Changed by */}
                  <div className="flex items-center gap-1.5 mb-2">
                    <svg className="w-3.5 h-3.5 flex-shrink-0" style={{ color: 'rgba(18,76,114,0.5)' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                    <span className="text-xs font-medium" style={{ color: 'rgba(18,76,114,0.7)' }}>
                      {entry.change_by || entry.changed_by || 'System'}
                    </span>
                  </div>

                  {/* Log description / notes */}
                  {entry.log_description || entry.logDescription ? (
                    <div
                      className="mt-2 px-3 py-2 rounded-lg text-sm text-gray-600 italic"
                      style={{ backgroundColor: 'rgba(18,76,114,0.04)', borderLeft: '3px solid rgba(18,76,114,0.2)' }}
                    >
                      "{entry.log_description || entry.logDescription}"
                    </div>
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    )}
  </div>
)}
          </div>
        </div>

        {/* Edit Modal */}
        {isModalVisible && selectedRequest && (
          <div
            className="fixed inset-0 bg-primary-800/40 flex justify-center items-center p-4"
            onClick={closeModal}
            aria-modal="true"
            role="dialog"
          >
            <div
              className="bg-white p-6 rounded-lg shadow-lg w-full max-w-4xl flex flex-col overflow-y-auto max-h-[90vh] space-y-4 relative"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex pb-6">
                <h2 className="text-2xl text-primary-800 text-center md:text-left">
                  {isEditMode
                    ? 'Edit Hiring Request'
                    : isSendApproveMode
                      ? 'Send Request Details'
                      : 'Request Details'}
                </h2>
                <button
                  onClick={closeModal}
                  className="ml-auto text-primary-800 bg-primary-800/20 hover:bg-primary-800 hover:text-white rounded-full w-8 h-8 flex items-center justify-center"
                  aria-label="Close"
                >
                  ✕
                </button>
              </div>

              {/* Tabs */}
              <div className="border-b border-gray-200">
                <nav className="flex gap-4 -mb-px flex-wrap">
                  {['details', 'jd'].map((tab) => (
                    <button
                      key={tab}
                      className={`px-4 py-2 font-medium text-sm rounded-t-lg transition-colors duration-300 ease-in-out ${activeTab === tab
                          ? 'text-white bg-primary-600'
                          : 'text-primary-800 bg-primary-600/30 hover:bg-gray-300'
                        }`}
                      onClick={() => setActiveTab(tab)}
                    >
                      {tab === 'details' ? 'Details' : 'Job Description'}
                    </button>
                  ))}
                </nav>
              </div>

              {/* Edit Mode — Details Tab */}
              {isEditMode && activeTab === 'details' && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-4">
                  <div className="flex flex-col">
                    <span className="text-gray-500 py-1 font-medium text-base">Reference Number</span>
                    <span className="text-gray-700 font-semibold break-words whitespace-normal">{selectedRequest.referenceNumber}</span>
                  </div>

                  <div className="flex flex-col">
                    <span className="text-gray-500 py-1 font-medium text-base">Request Type</span>
                    <span className="text-gray-700 font-semibold break-words whitespace-normal">{selectedRequest.request_type}</span>
                  </div>

                  <div className="flex flex-col">
                    <span className="text-gray-500 py-1 font-medium text-base">Department</span>
                    <select
                      className="w-full py-2 border px-2 border-primary-800/40 rounded-md text-gray-800 text-sm focus:outline-none"
                      value={selectedRequest.department_id}
                      onChange={(e) =>
                        setSelectedRequest({ ...selectedRequest, department_id: e.target.value })
                      }
                    >
                      <option value="">Select Department</option>
                      {departments?.map(dept => (
                        <option key={dept.department_id} value={dept.department_id}>
                          {dept.department_name}
                        </option>
                      ))}
                    </select>
                    {errors.department_id && <div className="text-red-500 text-xs mt-1">{errors.department_id}</div>}
                  </div>

                  <div className="flex flex-col">
                    <span className="text-gray-500 py-1 font-medium text-base">Role</span>
                    {(selectedRequest.request_type === "Exceptional" || selectedRequest.request_type === "Outsourced") ? (
                      <input
                        type="text"
                        className="w-full py-2 border px-2 border-primary-800/40 rounded-md text-gray-800 text-sm focus:outline-none"
                        value={selectedRequest.role_text || ""}
                        onChange={(e) =>
                          setSelectedRequest({ ...selectedRequest, role_text: e.target.value })
                        }
                      />
                    ) : (
                      <select
                        className="w-full py-2 border px-2 border-primary-800/40 rounded-md text-gray-800 text-sm focus:outline-none"
                        value={selectedRequest.demand_id}
                        onChange={(e) =>
                          setSelectedRequest({ ...selectedRequest, demand_id: e.target.value })
                        }
                      >
                        <option value="">Select Role</option>
                        {demandPlans?.map(plan => (
                          <option key={plan.demand_id} value={plan.demand_id}>
                            {plan.role}
                          </option>
                        ))}
                      </select>
                    )}
                    {errors.demand_id && selectedRequest.request_type === "Forecast" && (
                      <div className="text-red-500 text-xs mt-1">{errors.demand_id}</div>
                    )}
                    {errors.role_text && (selectedRequest.request_type === "Exceptional" || selectedRequest.request_type === "Outsourced") && (
                      <div className="text-red-500 text-xs mt-1">{errors.role_text}</div>
                    )}
                  </div>

                  <div className="flex flex-col">
                    <span className="text-gray-500 py-1 font-medium text-base">Position</span>
                    <input
                      type="text"
                      className="w-full py-2 border px-2 border-primary-800/40 rounded-md text-gray-800 text-sm focus:outline-none"
                      value={selectedRequest.position}
                      onChange={(e) =>
                        setSelectedRequest({ ...selectedRequest, position: e.target.value })
                      }
                    />
                    {errors.position && <div className="text-red-500 text-xs mt-1">{errors.position}</div>}
                  </div>

                  <div className="flex flex-col">
                    <span className="text-gray-500 py-1 font-medium text-base">Hiring Manager</span>
                    <select
                      className="w-full py-2 border px-2 border-primary-800/40 rounded-md text-gray-800 text-sm focus:outline-none"
                      value={selectedRequest.hiring_manager}
                      onChange={(e) =>
                        setSelectedRequest({ ...selectedRequest, hiring_manager: e.target.value })
                      }
                    >
                      <option value="">Select Hiring Manager</option>
                      {hiringManagers.map(manager => (
                        <option key={manager.user_id} value={manager.user_id}>
                          {manager.first_name} {manager.last_name}
                        </option>
                      ))}
                    </select>
                    {errors.hiring_manager && <div className="text-red-500 text-xs mt-1">{errors.hiring_manager}</div>}
                  </div>

                  <div className="flex flex-col">
                    <span className="text-gray-500 py-1 font-medium text-base">Planned Start Date</span>
                    <input
                      type="date"
                      className="w-full py-2 border px-2 border-primary-800/40 rounded-md text-gray-800 text-sm focus:outline-none"
                      value={selectedRequest.planned_start_date ? selectedRequest.planned_start_date.split('T')[0] : ''}
                      onChange={(e) =>
                        setSelectedRequest({ ...selectedRequest, planned_start_date: e.target.value })
                      }
                    />
                    {errors.planned_start_date && <div className="text-red-500 text-xs mt-1">{errors.planned_start_date}</div>}
                  </div>

                  <div className="flex flex-col">
                    <span className="text-gray-500 py-1 font-medium text-base">Priority</span>
                    <select
                      className="w-full py-2 border px-2 border-primary-800/40 rounded-md text-gray-800 text-sm focus:outline-none"
                      value={selectedRequest.priority}
                      onChange={(e) => setSelectedRequest({ ...selectedRequest, priority: e.target.value })}
                    >
                      <option value="">Select Priority</option>
                      <option value="Low">Low</option>
                      <option value="Medium">Medium</option>
                      <option value="High">High</option>
                    </select>
                    {errors.priority && <div className="text-red-500 text-xs mt-1">{errors.priority}</div>}
                  </div>

                  <div className="flex flex-col">
                    <span className="text-gray-500 py-1 font-medium text-base">Hiring Type</span>
                    <select
                      className="w-full py-2 border px-2 border-primary-800/40 rounded-md text-gray-800 text-sm focus:outline-none"
                      value={selectedRequest.hiring_Type}
                      onChange={(e) => setSelectedRequest({ ...selectedRequest, hiring_Type: e.target.value })}
                    >
                      <option value="">Select Hiring Type</option>
                      <option value="New">New</option>
                      {(selectedRequest.request_type === "Exceptional" || selectedRequest.request_type === "Outsourced") && (
                        <option value="Replacement">Replacement</option>
                      )}
                    </select>
                    {errors.hiring_Type && <div className="text-red-500 text-xs mt-1">{errors.hiring_Type}</div>}
                  </div>

                  <div className="col-span-1 md:col-span-3 flex flex-col mb-2">
                    <span className="text-gray-500 py-1 font-medium text-base">Reason for Hire</span>
                    <textarea
                      className="w-full py-2 border px-2 border-primary-800/40 rounded-md text-gray-800 text-sm focus:outline-none"
                      value={selectedRequest.reason_for_hire}
                      onChange={(e) =>
                        setSelectedRequest({ ...selectedRequest, reason_for_hire: e.target.value })
                      }
                    />
                    {errors.reason_for_hire && <div className="text-red-500 text-xs mt-1">{errors.reason_for_hire}</div>}
                  </div>

                  {(selectedRequest.request_type === "Exceptional" || selectedRequest.request_type === "Outsourced") && (
                    <div className="col-span-1 md:col-span-3 flex flex-col mb-2">
                      <span className="text-gray-500 px-2 py-1 font-medium text-base">Preferred Requirement</span>
                      <textarea
                        className="w-full px-2 py-1 border border-primary-800/40 rounded-md text-gray-800 text-sm break-words whitespace-normal"
                        value={selectedRequest.preferred_requirment}
                        onChange={(e) =>
                          setSelectedRequest({ ...selectedRequest, preferred_requirment: e.target.value })
                        }
                      />
                    </div>
                  )}
                </div>
              )}

              {/* Send for Approval Mode */}
              {isSendApproveMode && (
                <div className="pt-4">
                  <div className="mb-4">
                    <label className="text-gray-500 py-1 font-medium text-base">Notes for Approval</label>
                    <textarea
                      className="w-full py-2 border px-2 border-primary-800/40 rounded-md text-gray-800 text-sm focus:outline-none"
                      rows="4"
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      placeholder="Enter notes for approval..."
                    />
                    {errors.notes && <div className="text-red-500 text-xs mt-1">{errors.notes}</div>}
                  </div>
                </div>
              )}

              {/* JD Tab in Edit Mode */}
              {isEditMode && activeTab === 'jd' && (
                <div className="pt-4">
                  <JobDescription
                    activeTab={activeTab}
                    selectedRequest={
                      (selectedRequest.request_type === "Exceptional" || selectedRequest.request_type === "Outsourced")
                        ? selectedRequest
                        : {
                          ...demandPlans?.find(d => Number(d.demand_id) === Number(selectedRequest.demand_id)),
                          request_id: selectedRequest?.demand_id
                        }
                    }
                    isEditMode={isEditMode}
                    currentDocument={currentDocument || selectedRequest?.Documents}
                    processtype={
                      (selectedRequest.request_type === "Exceptional" || selectedRequest.request_type === "Outsourced")
                        ? "JobDescription"
                        : "ForeCastJobDescription"
                    }
                    onDelete={(doc) => setCurrentDocument(null)}
                    onUpload={(file) => setCurrentDocument(file)}
                  />
                </div>
              )}

              {/* Footer */}
              <div className="w-full flex flex-col sm:flex-row justify-end gap-2 mt-6 flex-wrap border-t border-gray-200 pt-4">
                <button
                  className="bg-primary-800/60 text-white px-4 py-2 rounded-md hover:bg-primary-800 w-full sm:w-auto"
                  onClick={closeModal}
                >
                  Cancel
                </button>
                {isEditMode && (
                  <button
                    className="text-white bg-primary-800 hover:bg-primary-800/60 px-4 py-2 rounded w-full sm:w-auto"
                    onClick={handleEditSave}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Saving...' : 'Save'}
                  </button>
                )}
                {isSendApproveMode && (
                  <button
                    className="text-white bg-primary-800 hover:bg-primary-800/60 px-4 py-2 rounded w-full sm:w-auto"
                    onClick={handleSendForApprovalSave}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Sending...' : 'Send for Approval'}
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Approve Modal */}
        {isApproveModalVisible && (
          <div className="fixed inset-0 backdrop-blur-sm flex justify-center items-center p-4 z-50" style={{ backgroundColor: 'rgba(18, 76, 114, 0.15)' }}>
            <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-full sm:max-w-lg md:max-w-xl lg:max-w-2xl mx-auto" style={{ borderColor: 'rgba(34, 197, 94, 0.2)', borderWidth: '1px', boxShadow: '0 20px 40px -15px rgba(34, 197, 94, 0.2)' }}>
              <div className="flex items-center justify-between p-5 sm:p-6 border-b" style={{ borderColor: 'rgba(34, 197, 94, 0.1)' }}>
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg" style={{ backgroundColor: 'rgba(34, 197, 94, 0.1)' }}>
                    <svg className="w-5 h-5" style={{ color: '#22c55e' }} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                  </div>
                  <div>
                    <h2 className="text-lg sm:text-xl font-semibold" style={{ color: 'rgb(18, 76, 114)' }}>Approve Request</h2>
                    <p className="text-xs sm:text-sm mt-0.5" style={{ color: 'rgba(18, 76, 114, 0.7)' }}>Confirm approval for this hiring request</p>
                  </div>
                </div>
                <button onClick={() => { setIsApproveModalVisible(false); setNotes(""); setErrors({}); }} className="flex-shrink-0 p-1.5 rounded-lg" style={{ color: 'rgba(18, 76, 114, 0.5)', backgroundColor: 'rgba(18, 76, 114, 0.05)' }}>
                  <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
              </div>
              <div className="p-5 sm:p-6 space-y-5">
                <div className="flex items-start gap-3 p-4 rounded-xl" style={{ backgroundColor: 'rgba(34, 197, 94, 0.05)', border: '1px solid rgba(34, 197, 94, 0.15)' }}>
                  <svg className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: '#22c55e' }} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                  <p className="text-sm text-gray-600">By approving, this request will move forward in the approval workflow.</p>
                </div>
                <div className="grid grid-cols-2 gap-3 p-4 rounded-xl" style={{ backgroundColor: 'rgba(18, 76, 114, 0.03)', border: '1px solid rgba(18, 76, 114, 0.08)' }}>
                  <div><span className="text-xs font-medium block" style={{ color: 'rgba(18, 76, 114, 0.6)' }}>Request ID</span><span className="text-sm font-semibold" style={{ color: 'rgb(18, 76, 114)' }}>{selectedRequest?.referenceNumber || 'N/A'}</span></div>
                  <div><span className="text-xs font-medium block" style={{ color: 'rgba(18, 76, 114, 0.6)' }}>Department</span><span className="text-sm font-semibold" style={{ color: 'rgb(18, 76, 114)' }}>{requestDeptName || requestDepartment?.department_name || 'N/A'}</span></div>
                  <div><span className="text-xs font-medium block" style={{ color: 'rgba(18, 76, 114, 0.6)' }}>Hiring Manager</span><span className="text-sm font-semibold" style={{ color: 'rgb(18, 76, 114)' }}>{requestManagerName || 'N/A'}</span></div>
                  <div><span className="text-xs font-medium block" style={{ color: 'rgba(18, 76, 114, 0.6)' }}>Position</span><span className="text-sm font-semibold" style={{ color: 'rgb(18, 76, 114)' }}>{selectedRequestFromState?.position || 'N/A'}</span></div>
                </div>
                <div className="space-y-2">
                  <label className="block text-sm font-medium" style={{ color: 'rgb(18, 76, 114)' }}>Notes</label>
                  <textarea className="w-full px-4 py-3.5 border rounded-xl outline-none transition text-gray-700 resize-vertical" rows="3" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Add notes about your approval..." style={{ borderColor: 'rgba(34, 197, 94, 0.3)', backgroundColor: 'white', fontSize: '14px' }} />
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 p-5 sm:p-6 border-t rounded-b-2xl" style={{ borderColor: 'rgba(34, 197, 94, 0.1)', backgroundColor: 'rgba(34, 197, 94, 0.02)' }}>
                <button onClick={() => { setIsApproveModalVisible(false); setNotes(""); setErrors({}); }} className="flex-1 px-4 py-3 bg-white border rounded-xl font-medium" style={{ color: 'rgb(18, 76, 114)', borderColor: 'rgba(18, 76, 114, 0.2)' }}>Cancel</button>
                <button onClick={handleApprove} disabled={isSubmitting} className="flex-1 px-4 py-3 text-white font-medium rounded-xl transition-all duration-200" style={{ backgroundColor: isSubmitting ? 'rgba(34, 197, 94, 0.5)' : '#22c55e', cursor: isSubmitting ? 'not-allowed' : 'pointer' }}>
                  {isSubmitting ? 'Approving...' : 'Approve Request'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── Finance Manager Approve Modal ─────────────────────────────── */}
        {isFinanceApproveModalVisible && (
          <div className="fixed inset-0 backdrop-blur-sm flex justify-center items-center p-4 z-50" style={{ backgroundColor: 'rgba(18, 76, 114, 0.15)' }}>
            <div
              className="relative bg-white rounded-2xl shadow-2xl w-full max-w-full sm:max-w-lg md:max-w-xl lg:max-w-2xl mx-auto"
              style={{ borderColor: 'rgba(16, 185, 129, 0.2)', borderWidth: '1px', boxShadow: '0 20px 40px -15px rgba(16, 185, 129, 0.2)' }}
            >
              {/* Header */}
              <div className="flex items-center justify-between p-5 sm:p-6 border-b" style={{ borderColor: 'rgba(16, 185, 129, 0.1)' }}>
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg" style={{ backgroundColor: 'rgba(16, 185, 129, 0.1)' }}>
                    <svg className="w-5 h-5" style={{ color: '#10b981' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div>
                    <h2 className="text-lg sm:text-xl font-semibold" style={{ color: 'rgb(18, 76, 114)' }}>Finance Manager Approval</h2>
                    <p className="text-xs sm:text-sm mt-0.5" style={{ color: 'rgba(18, 76, 114, 0.7)' }}>
                      Confirm finance approval for this {selectedRequestFromState.request_type} request
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => { setIsFinanceApproveModalVisible(false); setNotes(""); setErrors({}); }}
                  className="flex-shrink-0 p-1.5 rounded-lg"
                  style={{ color: 'rgba(18, 76, 114, 0.5)', backgroundColor: 'rgba(18, 76, 114, 0.05)' }}
                >
                  <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Body */}
              <div className="p-5 sm:p-6 space-y-5">
                <div className="flex items-start gap-3 p-4 rounded-xl" style={{ backgroundColor: 'rgba(16, 185, 129, 0.05)', border: '1px solid rgba(16, 185, 129, 0.15)' }}>
                  <svg className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: '#10b981' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <p className="text-sm text-gray-600">
                    By approving, you confirm that the budget and financial requirements for this{' '}
                    <span className="font-semibold" style={{ color: 'rgb(18, 76, 114)' }}>{selectedRequestFromState.request_type}</span>{' '}
                    request have been reviewed and approved. This will allow the request to be converted to sourcing.
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-3 p-4 rounded-xl" style={{ backgroundColor: 'rgba(18, 76, 114, 0.03)', border: '1px solid rgba(18, 76, 114, 0.08)' }}>
                  <div><span className="text-xs font-medium block" style={{ color: 'rgba(18, 76, 114, 0.6)' }}>Request ID</span><span className="text-sm font-semibold" style={{ color: 'rgb(18, 76, 114)' }}>{selectedRequest?.referenceNumber || 'N/A'}</span></div>
                  <div><span className="text-xs font-medium block" style={{ color: 'rgba(18, 76, 114, 0.6)' }}>Department</span><span className="text-sm font-semibold" style={{ color: 'rgb(18, 76, 114)' }}>{requestDeptName || requestDepartment?.department_name || 'N/A'}</span></div>
                  <div><span className="text-xs font-medium block" style={{ color: 'rgba(18, 76, 114, 0.6)' }}>Hiring Manager</span><span className="text-sm font-semibold" style={{ color: 'rgb(18, 76, 114)' }}>{requestManagerName || 'N/A'}</span></div>
                  <div><span className="text-xs font-medium block" style={{ color: 'rgba(18, 76, 114, 0.6)' }}>Position</span><span className="text-sm font-semibold" style={{ color: 'rgb(18, 76, 114)' }}>{selectedRequestFromState?.position || 'N/A'}</span></div>
                </div>

                <div className="space-y-2">
                  <label className="block text-sm font-medium" style={{ color: 'rgb(18, 76, 114)' }}>
                    Notes <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    className="w-full px-4 py-3.5 border rounded-xl outline-none transition text-gray-700 resize-vertical"
                    rows="3"
                    value={notes}
                    onChange={(e) => { setNotes(e.target.value); if (errors.notes) setErrors({}); }}
                    placeholder="Add notes about your finance approval..."
                    style={{ borderColor: errors.notes ? '#ef4444' : 'rgba(16, 185, 129, 0.3)', backgroundColor: 'white', fontSize: '14px' }}
                  />
                  {errors.notes && <div className="text-red-500 text-xs mt-1">{errors.notes}</div>}
                </div>
              </div>

              {/* Footer */}
              <div className="flex flex-col sm:flex-row gap-3 p-5 sm:p-6 border-t rounded-b-2xl" style={{ borderColor: 'rgba(16, 185, 129, 0.1)', backgroundColor: 'rgba(16, 185, 129, 0.02)' }}>
                <button
                  onClick={() => { setIsFinanceApproveModalVisible(false); setNotes(""); setErrors({}); }}
                  className="flex-1 px-4 py-3 bg-white border rounded-xl font-medium"
                  style={{ color: 'rgb(18, 76, 114)', borderColor: 'rgba(18, 76, 114, 0.2)' }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleFinanceApprove}
                  disabled={isSubmitting}
                  className="flex-1 px-4 py-3 text-white font-medium rounded-xl transition-all duration-200"
                  style={{ backgroundColor: isSubmitting ? 'rgba(16, 185, 129, 0.5)' : '#10b981', cursor: isSubmitting ? 'not-allowed' : 'pointer' }}
                >
                  {isSubmitting ? 'Approving...' : 'Confirm Finance Approval'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── HR Director Approve Modal ──────────────────────────────── */}
        {isHRDirectorApproveModalVisible && (
          <div className="fixed inset-0 backdrop-blur-sm flex justify-center items-center p-4 z-50" style={{ backgroundColor: 'rgba(18, 76, 114, 0.15)' }}>
            <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-full sm:max-w-lg md:max-w-xl lg:max-w-2xl mx-auto" style={{ borderColor: 'rgba(59, 130, 246, 0.2)', borderWidth: '1px', boxShadow: '0 20px 40px -15px rgba(59, 130, 246, 0.2)' }}>
              <div className="flex items-center justify-between p-5 sm:p-6 border-b" style={{ borderColor: 'rgba(59, 130, 246, 0.1)' }}>
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg" style={{ backgroundColor: 'rgba(59, 130, 246, 0.1)' }}>
                    <svg className="w-5 h-5" style={{ color: '#3b82f6' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div>
                    <h2 className="text-lg sm:text-xl font-semibold" style={{ color: 'rgb(18, 76, 114)' }}>HR Director Approval</h2>
                    <p className="text-xs sm:text-sm mt-0.5" style={{ color: 'rgba(18, 76, 114, 0.7)' }}>
                      Confirm HR Director approval for this {selectedRequestFromState.request_type} request
                    </p>
                  </div>
                </div>
                <button onClick={() => { setIsHRDirectorApproveModalVisible(false); setNotes(""); setErrors({}); }} className="flex-shrink-0 p-1.5 rounded-lg" style={{ color: 'rgba(18, 76, 114, 0.5)', backgroundColor: 'rgba(18, 76, 114, 0.05)' }}>
                  <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
              </div>
              <div className="p-5 sm:p-6 space-y-5">
                <div className="flex items-start gap-3 p-4 rounded-xl" style={{ backgroundColor: 'rgba(59, 130, 246, 0.05)', border: '1px solid rgba(59, 130, 246, 0.15)' }}>
                  <svg className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: '#3b82f6' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <p className="text-sm text-gray-600">
                    By approving, you confirm that this{' '}
                    <span className="font-semibold" style={{ color: 'rgb(18, 76, 114)' }}>{selectedRequestFromState.request_type}</span>{' '}
                    request has been reviewed. This will move the request to CEO for final approval.
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-3 p-4 rounded-xl" style={{ backgroundColor: 'rgba(18, 76, 114, 0.03)', border: '1px solid rgba(18, 76, 114, 0.08)' }}>
                  <div><span className="text-xs font-medium block" style={{ color: 'rgba(18, 76, 114, 0.6)' }}>Request ID</span><span className="text-sm font-semibold" style={{ color: 'rgb(18, 76, 114)' }}>{selectedRequest?.referenceNumber || 'N/A'}</span></div>
                  <div><span className="text-xs font-medium block" style={{ color: 'rgba(18, 76, 114, 0.6)' }}>Department</span><span className="text-sm font-semibold" style={{ color: 'rgb(18, 76, 114)' }}>{requestDeptName || requestDepartment?.department_name || 'N/A'}</span></div>
                  <div><span className="text-xs font-medium block" style={{ color: 'rgba(18, 76, 114, 0.6)' }}>Hiring Manager</span><span className="text-sm font-semibold" style={{ color: 'rgb(18, 76, 114)' }}>{requestManagerName || 'N/A'}</span></div>
                  <div><span className="text-xs font-medium block" style={{ color: 'rgba(18, 76, 114, 0.6)' }}>Position</span><span className="text-sm font-semibold" style={{ color: 'rgb(18, 76, 114)' }}>{selectedRequestFromState?.position || 'N/A'}</span></div>
                </div>
                <div className="space-y-2">
                  <label className="block text-sm font-medium" style={{ color: 'rgb(18, 76, 114)' }}>Notes <span className="text-red-500">*</span></label>
                  <textarea className="w-full px-4 py-3.5 border rounded-xl outline-none transition text-gray-700 resize-vertical" rows="3" value={notes} onChange={(e) => { setNotes(e.target.value); if (errors.notes) setErrors({}); }} placeholder="Add notes about your approval..." style={{ borderColor: errors.notes ? '#ef4444' : 'rgba(59, 130, 246, 0.3)', backgroundColor: 'white', fontSize: '14px' }} />
                  {errors.notes && <div className="text-red-500 text-xs mt-1">{errors.notes}</div>}
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 p-5 sm:p-6 border-t rounded-b-2xl" style={{ borderColor: 'rgba(59, 130, 246, 0.1)', backgroundColor: 'rgba(59, 130, 246, 0.02)' }}>
                <button onClick={() => { setIsHRDirectorApproveModalVisible(false); setNotes(""); setErrors({}); }} className="flex-1 px-4 py-3 bg-white border rounded-xl font-medium" style={{ color: 'rgb(18, 76, 114)', borderColor: 'rgba(18, 76, 114, 0.2)' }}>Cancel</button>
                <button onClick={handleHRDirectorApprove} disabled={isSubmitting} className="flex-1 px-4 py-3 text-white font-medium rounded-xl transition-all duration-200" style={{ backgroundColor: isSubmitting ? 'rgba(59, 130, 246, 0.5)' : '#3b82f6', cursor: isSubmitting ? 'not-allowed' : 'pointer' }}>
                  {isSubmitting ? 'Approving...' : 'Confirm HR Director Approval'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── CEO Approve Modal ──────────────────────────────── */}
        {isCEOApproveModalVisible && (
          <div className="fixed inset-0 backdrop-blur-sm flex justify-center items-center p-4 z-50" style={{ backgroundColor: 'rgba(18, 76, 114, 0.15)' }}>
            <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-full sm:max-w-lg md:max-w-xl lg:max-w-2xl mx-auto" style={{ borderColor: 'rgba(99, 102, 241, 0.2)', borderWidth: '1px', boxShadow: '0 20px 40px -15px rgba(99, 102, 241, 0.2)' }}>
              <div className="flex items-center justify-between p-5 sm:p-6 border-b" style={{ borderColor: 'rgba(99, 102, 241, 0.1)' }}>
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg" style={{ backgroundColor: 'rgba(99, 102, 241, 0.1)' }}>
                    <svg className="w-5 h-5" style={{ color: '#6366f1' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div>
                    <h2 className="text-lg sm:text-xl font-semibold" style={{ color: 'rgb(18, 76, 114)' }}>CEO Approval</h2>
                    <p className="text-xs sm:text-sm mt-0.5" style={{ color: 'rgba(18, 76, 114, 0.7)' }}>
                      Confirm CEO approval for this {selectedRequestFromState.request_type} request
                    </p>
                  </div>
                </div>
                <button onClick={() => { setIsCEOApproveModalVisible(false); setNotes(""); setErrors({}); }} className="flex-shrink-0 p-1.5 rounded-lg" style={{ color: 'rgba(18, 76, 114, 0.5)', backgroundColor: 'rgba(18, 76, 114, 0.05)' }}>
                  <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
              </div>
              <div className="p-5 sm:p-6 space-y-5">
                <div className="flex items-start gap-3 p-4 rounded-xl" style={{ backgroundColor: 'rgba(99, 102, 241, 0.05)', border: '1px solid rgba(99, 102, 241, 0.15)' }}>
                  <svg className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: '#6366f1' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <p className="text-sm text-gray-600">
                    By approving, you give final executive approval for this{' '}
                    <span className="font-semibold" style={{ color: 'rgb(18, 76, 114)' }}>{selectedRequestFromState.request_type}</span>{' '}
                    request. After this, the request can be converted to sourcing.
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-3 p-4 rounded-xl" style={{ backgroundColor: 'rgba(18, 76, 114, 0.03)', border: '1px solid rgba(18, 76, 114, 0.08)' }}>
                  <div><span className="text-xs font-medium block" style={{ color: 'rgba(18, 76, 114, 0.6)' }}>Request ID</span><span className="text-sm font-semibold" style={{ color: 'rgb(18, 76, 114)' }}>{selectedRequest?.referenceNumber || 'N/A'}</span></div>
                  <div><span className="text-xs font-medium block" style={{ color: 'rgba(18, 76, 114, 0.6)' }}>Department</span><span className="text-sm font-semibold" style={{ color: 'rgb(18, 76, 114)' }}>{requestDeptName || requestDepartment?.department_name || 'N/A'}</span></div>
                  <div><span className="text-xs font-medium block" style={{ color: 'rgba(18, 76, 114, 0.6)' }}>Hiring Manager</span><span className="text-sm font-semibold" style={{ color: 'rgb(18, 76, 114)' }}>{requestManagerName || 'N/A'}</span></div>
                  <div><span className="text-xs font-medium block" style={{ color: 'rgba(18, 76, 114, 0.6)' }}>Position</span><span className="text-sm font-semibold" style={{ color: 'rgb(18, 76, 114)' }}>{selectedRequestFromState?.position || 'N/A'}</span></div>
                </div>
                <div className="space-y-2">
                  <label className="block text-sm font-medium" style={{ color: 'rgb(18, 76, 114)' }}>Notes <span className="text-red-500">*</span></label>
                  <textarea className="w-full px-4 py-3.5 border rounded-xl outline-none transition text-gray-700 resize-vertical" rows="3" value={notes} onChange={(e) => { setNotes(e.target.value); if (errors.notes) setErrors({}); }} placeholder="Add notes about your approval..." style={{ borderColor: errors.notes ? '#ef4444' : 'rgba(99, 102, 241, 0.3)', backgroundColor: 'white', fontSize: '14px' }} />
                  {errors.notes && <div className="text-red-500 text-xs mt-1">{errors.notes}</div>}
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 p-5 sm:p-6 border-t rounded-b-2xl" style={{ borderColor: 'rgba(99, 102, 241, 0.1)', backgroundColor: 'rgba(99, 102, 241, 0.02)' }}>
                <button onClick={() => { setIsCEOApproveModalVisible(false); setNotes(""); setErrors({}); }} className="flex-1 px-4 py-3 bg-white border rounded-xl font-medium" style={{ color: 'rgb(18, 76, 114)', borderColor: 'rgba(18, 76, 114, 0.2)' }}>Cancel</button>
                <button onClick={handleCEOApprove} disabled={isSubmitting} className="flex-1 px-4 py-3 text-white font-medium rounded-xl transition-all duration-200" style={{ backgroundColor: isSubmitting ? 'rgba(99, 102, 241, 0.5)' : '#6366f1', cursor: isSubmitting ? 'not-allowed' : 'pointer' }}>
                  {isSubmitting ? 'Approving...' : 'Confirm CEO Approval'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── HR Director Reject Modal ──────────────────────────────── */}
        {isHRDirectorRejectModalVisible && (
          <div className="fixed inset-0 backdrop-blur-sm flex justify-center items-center p-4 z-50" style={{ backgroundColor: 'rgba(18, 76, 114, 0.15)' }}>
            <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-full sm:max-w-lg md:max-w-xl lg:max-w-2xl mx-auto" style={{ borderColor: 'rgba(239, 68, 68, 0.2)', borderWidth: '1px', boxShadow: '0 20px 40px -15px rgba(239, 68, 68, 0.2)' }}>
              <div className="flex items-center justify-between p-5 sm:p-6 border-b" style={{ borderColor: 'rgba(239, 68, 68, 0.1)' }}>
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg" style={{ backgroundColor: 'rgba(239, 68, 68, 0.1)' }}>
                    <svg className="w-5 h-5" style={{ color: '#ef4444' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div>
                    <h2 className="text-lg sm:text-xl font-semibold" style={{ color: 'rgb(18, 76, 114)' }}>HR Director Rejection</h2>
                    <p className="text-xs sm:text-sm mt-0.5" style={{ color: 'rgba(18, 76, 114, 0.7)' }}>This will reject the entire request</p>
                  </div>
                </div>
                <button onClick={() => { setIsHRDirectorRejectModalVisible(false); setNotes(""); setErrors({}); }} className="flex-shrink-0 p-1.5 rounded-lg" style={{ color: 'rgba(18, 76, 114, 0.5)', backgroundColor: 'rgba(18, 76, 114, 0.05)' }}>
                  <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
              </div>
              <div className="p-5 sm:p-6 space-y-5">
                <div className="flex items-start gap-3 p-4 rounded-xl" style={{ backgroundColor: 'rgba(239, 68, 68, 0.05)', border: '1px solid rgba(239, 68, 68, 0.15)' }}>
                  <svg className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: '#ef4444' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                  <p className="text-sm text-gray-600">This action will <strong className="text-red-600">reject the entire request</strong> and cannot be undone. Please provide a reason for rejection.</p>
                </div>
                <div className="space-y-2">
                  <label className="block text-sm font-medium" style={{ color: 'rgb(18, 76, 114)' }}>Reason for Rejection <span className="text-red-500">*</span></label>
                  <textarea className="w-full px-4 py-3.5 border rounded-xl outline-none transition text-gray-700 resize-vertical" rows="3" value={notes} onChange={(e) => { setNotes(e.target.value); if (errors.notes) setErrors({}); }} placeholder="Enter reason for rejection..." style={{ borderColor: errors.notes ? '#ef4444' : 'rgba(239, 68, 68, 0.3)', backgroundColor: 'white', fontSize: '14px' }} />
                  {errors.notes && <div className="text-red-500 text-xs mt-1">{errors.notes}</div>}
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 p-5 sm:p-6 border-t rounded-b-2xl" style={{ borderColor: 'rgba(239, 68, 68, 0.1)', backgroundColor: 'rgba(239, 68, 68, 0.02)' }}>
                <button onClick={() => { setIsHRDirectorRejectModalVisible(false); setNotes(""); setErrors({}); }} className="flex-1 px-4 py-3 bg-white border rounded-xl font-medium" style={{ color: 'rgb(18, 76, 114)', borderColor: 'rgba(18, 76, 114, 0.2)' }}>Cancel</button>
                <button onClick={handleHRDirectorReject} disabled={isSubmitting} className="flex-1 px-4 py-3 text-white font-medium rounded-xl transition-all duration-200" style={{ backgroundColor: isSubmitting ? 'rgba(239, 68, 68, 0.5)' : '#ef4444', cursor: isSubmitting ? 'not-allowed' : 'pointer' }}>
                  {isSubmitting ? 'Rejecting...' : 'Confirm Rejection'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── CEO Reject Modal ──────────────────────────────── */}
        {isCEORejectModalVisible && (
          <div className="fixed inset-0 backdrop-blur-sm flex justify-center items-center p-4 z-50" style={{ backgroundColor: 'rgba(18, 76, 114, 0.15)' }}>
            <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-full sm:max-w-lg md:max-w-xl lg:max-w-2xl mx-auto" style={{ borderColor: 'rgba(239, 68, 68, 0.2)', borderWidth: '1px', boxShadow: '0 20px 40px -15px rgba(239, 68, 68, 0.2)' }}>
              <div className="flex items-center justify-between p-5 sm:p-6 border-b" style={{ borderColor: 'rgba(239, 68, 68, 0.1)' }}>
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg" style={{ backgroundColor: 'rgba(239, 68, 68, 0.1)' }}>
                    <svg className="w-5 h-5" style={{ color: '#ef4444' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div>
                    <h2 className="text-lg sm:text-xl font-semibold" style={{ color: 'rgb(18, 76, 114)' }}>CEO Rejection</h2>
                    <p className="text-xs sm:text-sm mt-0.5" style={{ color: 'rgba(18, 76, 114, 0.7)' }}>This will reject the entire request</p>
                  </div>
                </div>
                <button onClick={() => { setIsCEORejectModalVisible(false); setNotes(""); setErrors({}); }} className="flex-shrink-0 p-1.5 rounded-lg" style={{ color: 'rgba(18, 76, 114, 0.5)', backgroundColor: 'rgba(18, 76, 114, 0.05)' }}>
                  <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
              </div>
              <div className="p-5 sm:p-6 space-y-5">
                <div className="flex items-start gap-3 p-4 rounded-xl" style={{ backgroundColor: 'rgba(239, 68, 68, 0.05)', border: '1px solid rgba(239, 68, 68, 0.15)' }}>
                  <svg className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: '#ef4444' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                  <p className="text-sm text-gray-600">This action will <strong className="text-red-600">reject the entire request</strong> and cannot be undone. Please provide a reason for rejection.</p>
                </div>
                <div className="space-y-2">
                  <label className="block text-sm font-medium" style={{ color: 'rgb(18, 76, 114)' }}>Reason for Rejection <span className="text-red-500">*</span></label>
                  <textarea className="w-full px-4 py-3.5 border rounded-xl outline-none transition text-gray-700 resize-vertical" rows="3" value={notes} onChange={(e) => { setNotes(e.target.value); if (errors.notes) setErrors({}); }} placeholder="Enter reason for rejection..." style={{ borderColor: errors.notes ? '#ef4444' : 'rgba(239, 68, 68, 0.3)', backgroundColor: 'white', fontSize: '14px' }} />
                  {errors.notes && <div className="text-red-500 text-xs mt-1">{errors.notes}</div>}
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 p-5 sm:p-6 border-t rounded-b-2xl" style={{ borderColor: 'rgba(239, 68, 68, 0.1)', backgroundColor: 'rgba(239, 68, 68, 0.02)' }}>
                <button onClick={() => { setIsCEORejectModalVisible(false); setNotes(""); setErrors({}); }} className="flex-1 px-4 py-3 bg-white border rounded-xl font-medium" style={{ color: 'rgb(18, 76, 114)', borderColor: 'rgba(18, 76, 114, 0.2)' }}>Cancel</button>
                <button onClick={handleCEOReject} disabled={isSubmitting} className="flex-1 px-4 py-3 text-white font-medium rounded-xl transition-all duration-200" style={{ backgroundColor: isSubmitting ? 'rgba(239, 68, 68, 0.5)' : '#ef4444', cursor: isSubmitting ? 'not-allowed' : 'pointer' }}>
                  {isSubmitting ? 'Rejecting...' : 'Confirm Rejection'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── Finance Manager Reject Modal ──────────────────────────────── */}
        {isFinanceRejectModalVisible && (
          <div className="fixed inset-0 backdrop-blur-sm flex justify-center items-center p-4 z-50" style={{ backgroundColor: 'rgba(18, 76, 114, 0.15)' }}>
            <div
              className="relative bg-white rounded-2xl shadow-2xl w-full max-w-full sm:max-w-lg md:max-w-xl lg:max-w-2xl mx-auto"
              style={{ borderColor: 'rgba(244, 63, 94, 0.2)', borderWidth: '1px', boxShadow: '0 20px 40px -15px rgba(244, 63, 94, 0.2)' }}
            >
              {/* Header */}
              <div className="flex items-center justify-between p-5 sm:p-6 border-b" style={{ borderColor: 'rgba(244, 63, 94, 0.1)' }}>
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg" style={{ backgroundColor: 'rgba(244, 63, 94, 0.1)' }}>
                    <svg className="w-5 h-5" style={{ color: '#f43f5e' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div>
                    <h2 className="text-lg sm:text-xl font-semibold" style={{ color: 'rgb(18, 76, 114)' }}>Finance Manager Rejection</h2>
                    <p className="text-xs sm:text-sm mt-0.5" style={{ color: 'rgba(18, 76, 114, 0.7)' }}>
                      Reject this {selectedRequestFromState.request_type} request on financial grounds
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => { setIsFinanceRejectModalVisible(false); setNotes(""); setErrors({}); }}
                  className="flex-shrink-0 p-1.5 rounded-lg"
                  style={{ color: 'rgba(18, 76, 114, 0.5)', backgroundColor: 'rgba(18, 76, 114, 0.05)' }}
                >
                  <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Body */}
              <div className="p-5 sm:p-6 space-y-5">
                <div className="flex items-start gap-3 p-4 rounded-xl" style={{ backgroundColor: 'rgba(244, 63, 94, 0.05)', border: '1px solid rgba(244, 63, 94, 0.15)' }}>
                  <svg className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: '#f43f5e' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.998-.833-2.732 0L4.342 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                  <p className="text-sm text-gray-600">
                    Rejecting this request will set its status to <span className="font-semibold text-red-500">Rejected</span> and notify the hiring manager. Please provide a clear financial reason for the rejection.
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-3 p-4 rounded-xl" style={{ backgroundColor: 'rgba(18, 76, 114, 0.03)', border: '1px solid rgba(18, 76, 114, 0.08)' }}>
                  <div><span className="text-xs font-medium block" style={{ color: 'rgba(18, 76, 114, 0.6)' }}>Request ID</span><span className="text-sm font-semibold" style={{ color: 'rgb(18, 76, 114)' }}>{selectedRequest?.referenceNumber || 'N/A'}</span></div>
                  <div><span className="text-xs font-medium block" style={{ color: 'rgba(18, 76, 114, 0.6)' }}>Department</span><span className="text-sm font-semibold" style={{ color: 'rgb(18, 76, 114)' }}>{requestDeptName || requestDepartment?.department_name || 'N/A'}</span></div>
                  <div><span className="text-xs font-medium block" style={{ color: 'rgba(18, 76, 114, 0.6)' }}>Hiring Manager</span><span className="text-sm font-semibold" style={{ color: 'rgb(18, 76, 114)' }}>{requestManagerName || 'N/A'}</span></div>
                  <div><span className="text-xs font-medium block" style={{ color: 'rgba(18, 76, 114, 0.6)' }}>Position</span><span className="text-sm font-semibold" style={{ color: 'rgb(18, 76, 114)' }}>{selectedRequestFromState?.position || 'N/A'}</span></div>
                </div>

                <div className="space-y-2">
                  <label className="block text-sm font-medium" style={{ color: 'rgb(18, 76, 114)' }}>
                    Reason for Rejection <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    className="w-full px-4 py-3.5 border rounded-xl outline-none transition text-gray-700 resize-vertical"
                    rows="4"
                    value={notes}
                    onChange={(e) => { setNotes(e.target.value); if (errors.notes) setErrors({}); }}
                    placeholder="Provide a detailed financial reason for rejecting this request..."
                    style={{ borderColor: errors.notes ? '#ef4444' : 'rgba(244, 63, 94, 0.3)', backgroundColor: 'white', fontSize: '14px' }}
                  />
                  {errors.notes && <div className="text-red-500 text-xs mt-1">{errors.notes}</div>}
                </div>
              </div>

              {/* Footer */}
              <div className="flex flex-col sm:flex-row gap-3 p-5 sm:p-6 border-t rounded-b-2xl" style={{ borderColor: 'rgba(244, 63, 94, 0.1)', backgroundColor: 'rgba(244, 63, 94, 0.02)' }}>
                <button
                  onClick={() => { setIsFinanceRejectModalVisible(false); setNotes(""); setErrors({}); }}
                  className="flex-1 px-4 py-3 bg-white border rounded-xl font-medium"
                  style={{ color: 'rgb(18, 76, 114)', borderColor: 'rgba(18, 76, 114, 0.2)' }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleFinanceReject}
                  disabled={isSubmitting || !notes.trim()}
                  className="flex-1 px-4 py-3 text-white font-medium rounded-xl transition-all duration-200"
                  style={{ backgroundColor: !notes.trim() || isSubmitting ? 'rgba(244, 63, 94, 0.4)' : '#f43f5e', cursor: !notes.trim() || isSubmitting ? 'not-allowed' : 'pointer' }}
                >
                  {isSubmitting ? 'Rejecting...' : 'Confirm Finance Rejection'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Convert to Sourcing Modal */}
        {isConfirmModalVisible && (
          <div className="fixed inset-0 backdrop-blur-sm flex justify-center items-center p-4 z-50" style={{ backgroundColor: 'rgba(18, 76, 114, 0.15)' }}>
            <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-full sm:max-w-lg md:max-w-xl lg:max-w-2xl mx-auto" style={{ borderColor: 'rgba(18, 76, 114, 0.15)', borderWidth: '1px' }}>
              <div className="flex items-center justify-between p-5 sm:p-6 border-b" style={{ borderColor: 'rgba(18, 76, 114, 0.08)' }}>
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg" style={{ backgroundColor: 'rgba(168, 85, 247, 0.1)' }}>
                    <svg className="w-5 h-5" style={{ color: '#a855f7' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                    </svg>
                  </div>
                  <h2 className="text-lg sm:text-xl font-semibold" style={{ color: 'rgb(18, 76, 114)' }}>Convert to Sourcing</h2>
                </div>
                <button
                  onClick={() => { setIsConfirmModalVisible(false); setSelectedRecruiter(null); setErrors({}); }}
                  className="flex-shrink-0 p-1.5 rounded-lg"
                  style={{ color: 'rgba(18, 76, 114, 0.5)', backgroundColor: 'rgba(18, 76, 114, 0.05)' }}
                >
                  <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div className="p-5 sm:p-6 space-y-6">
                <div className="space-y-2.5">
                  <label className="block text-sm font-medium" style={{ color: 'rgb(18, 76, 114)' }}>Select Recruiter</label>
                  <Select
                    options={hiringManagers
                      .filter(manager =>
                        manager.userCompanies?.some?.(uc => uc.role === "HR Specialist") ||
                        manager.role === "HR Specialist"
                      )
                      .map(manager => ({
                        value: manager.user_id,
                        label: `${manager.first_name} ${manager.last_name}`,
                        email: manager.email
                      }))}
                    value={selectedRecruiter ? {
                      value: selectedRecruiter,
                      label: `${hiringManagers.find(m => m.user_id === selectedRecruiter)?.first_name} ${hiringManagers.find(m => m.user_id === selectedRecruiter)?.last_name}`
                    } : null}
                    onChange={handleRecruiterChange}
                    placeholder="Search recruiter..."
                    isSearchable
                    className="w-full"
                  />
                  {errors.recruiter && (
                    <div className="text-red-500 text-xs mt-1">{errors.recruiter}</div>
                  )}
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 p-5 sm:p-6 border-t rounded-b-2xl" style={{ borderColor: 'rgba(18, 76, 114, 0.08)', backgroundColor: 'rgba(18, 76, 114, 0.02)' }}>
                <button
                  onClick={() => { setIsConfirmModalVisible(false); setSelectedRecruiter(null); setErrors({}); }}
                  className="flex-1 px-4 py-3 bg-white border rounded-xl font-medium"
                  style={{ color: 'rgb(18, 76, 114)', borderColor: 'rgba(18, 76, 114, 0.2)' }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleConvertToSourcing}
                  disabled={isSubmitting || !selectedRecruiter}
                  className="flex-1 px-4 py-3 text-white font-medium rounded-xl"
                  style={{ backgroundColor: !selectedRecruiter || isSubmitting ? 'rgba(168, 85, 247, 0.5)' : '#a855f7', cursor: !selectedRecruiter || isSubmitting ? 'not-allowed' : 'pointer' }}
                >
                  {isSubmitting ? 'Converting...' : 'Convert to Sourcing'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Reject Modal */}
        {isRejectModalVisible && (
          <div className="fixed inset-0 backdrop-blur-sm flex justify-center items-center p-4 z-50" style={{ backgroundColor: 'rgba(18, 76, 114, 0.15)' }}>
            <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-full sm:max-w-lg md:max-w-xl lg:max-w-2xl mx-auto" style={{ borderColor: 'rgba(239, 68, 68, 0.2)', borderWidth: '1px', boxShadow: '0 20px 40px -15px rgba(239, 68, 68, 0.2)' }}>
              <div className="flex items-center justify-between p-5 sm:p-6 border-b" style={{ borderColor: 'rgba(239, 68, 68, 0.1)' }}>
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg" style={{ backgroundColor: 'rgba(239, 68, 68, 0.1)' }}>
                    <svg className="w-5 h-5" style={{ color: '#ef4444' }} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                  </div>
                  <div>
                    <h2 className="text-lg sm:text-xl font-semibold" style={{ color: 'rgb(18, 76, 114)' }}>Reject Request</h2>
                    <p className="text-xs sm:text-sm mt-0.5" style={{ color: 'rgba(18, 76, 114, 0.7)' }}>This will reject the entire request</p>
                  </div>
                </div>
                <button onClick={() => { setIsRejectModalVisible(false); setNotes(""); setErrors({}); }} className="flex-shrink-0 p-1.5 rounded-lg" style={{ color: 'rgba(18, 76, 114, 0.5)', backgroundColor: 'rgba(18, 76, 114, 0.05)' }}>
                  <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
              </div>
              <div className="p-5 sm:p-6 space-y-5">
                <div className="flex items-start gap-3 p-4 rounded-xl" style={{ backgroundColor: 'rgba(239, 68, 68, 0.05)', border: '1px solid rgba(239, 68, 68, 0.15)' }}>
                  <svg className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: '#ef4444' }} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" /></svg>
                  <p className="text-sm text-gray-600">This action will <strong className="text-red-600">reject the entire request</strong> and cannot be undone.</p>
                </div>
                <div className="grid grid-cols-2 gap-3 p-4 rounded-xl" style={{ backgroundColor: 'rgba(18, 76, 114, 0.03)', border: '1px solid rgba(18, 76, 114, 0.08)' }}>
                  <div><span className="text-xs font-medium block" style={{ color: 'rgba(18, 76, 114, 0.6)' }}>Request ID</span><span className="text-sm font-semibold" style={{ color: 'rgb(18, 76, 114)' }}>{selectedRequest?.referenceNumber || 'N/A'}</span></div>
                  <div><span className="text-xs font-medium block" style={{ color: 'rgba(18, 76, 114, 0.6)' }}>Department</span><span className="text-sm font-semibold" style={{ color: 'rgb(18, 76, 114)' }}>{requestDeptName || requestDepartment?.department_name || 'N/A'}</span></div>
                  <div><span className="text-xs font-medium block" style={{ color: 'rgba(18, 76, 114, 0.6)' }}>Hiring Manager</span><span className="text-sm font-semibold" style={{ color: 'rgb(18, 76, 114)' }}>{requestManagerName || 'N/A'}</span></div>
                  <div><span className="text-xs font-medium block" style={{ color: 'rgba(18, 76, 114, 0.6)' }}>Position</span><span className="text-sm font-semibold" style={{ color: 'rgb(18, 76, 114)' }}>{selectedRequestFromState?.position || 'N/A'}</span></div>
                </div>
                <div className="space-y-2">
                  <label className="block text-sm font-medium" style={{ color: 'rgb(18, 76, 114)' }}>Reason for Rejection <span className="text-red-500">*</span></label>
                  <textarea className="w-full px-4 py-3.5 border rounded-xl outline-none transition text-gray-700 resize-vertical" rows="3" value={notes} onChange={(e) => { setNotes(e.target.value); if (errors.notes) setErrors({}); }} placeholder="Provide a reason for rejection..." style={{ borderColor: errors.notes ? '#ef4444' : 'rgba(239, 68, 68, 0.3)', backgroundColor: 'white', fontSize: '14px' }} />
                  {errors.notes && <div className="text-red-500 text-xs mt-1">{errors.notes}</div>}
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 p-5 sm:p-6 border-t rounded-b-2xl" style={{ borderColor: 'rgba(239, 68, 68, 0.1)', backgroundColor: 'rgba(239, 68, 68, 0.02)' }}>
                <button onClick={() => { setIsRejectModalVisible(false); setNotes(""); setErrors({}); }} className="flex-1 px-4 py-3 bg-white border rounded-xl font-medium" style={{ color: 'rgb(18, 76, 114)', borderColor: 'rgba(18, 76, 114, 0.2)' }}>Cancel</button>
                <button onClick={handleReject} disabled={isSubmitting || !notes.trim()} className="flex-1 px-4 py-3 text-white font-medium rounded-xl" style={{ backgroundColor: !notes.trim() || isSubmitting ? 'rgba(239, 68, 68, 0.5)' : '#ef4444', cursor: !notes.trim() || isSubmitting ? 'not-allowed' : 'pointer' }}>
                  {isSubmitting ? 'Rejecting...' : 'Confirm Rejection'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Hold Modal */}
        {isHoldModalVisible && (
          <div className="fixed inset-0 backdrop-blur-sm flex justify-center items-center p-4 z-50" style={{ backgroundColor: 'rgba(18, 76, 114, 0.15)' }}>
            <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-full sm:max-w-lg md:max-w-xl lg:max-w-2xl mx-auto" style={{ borderColor: 'rgba(245, 158, 11, 0.2)', borderWidth: '1px', boxShadow: '0 20px 40px -15px rgba(245, 158, 11, 0.2)' }}>
              <div className="flex items-center justify-between p-5 sm:p-6 border-b" style={{ borderColor: 'rgba(245, 158, 11, 0.1)' }}>
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg" style={{ backgroundColor: 'rgba(245, 158, 11, 0.1)' }}>
                    <svg className="w-5 h-5" style={{ color: '#f59e0b' }} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                  </div>
                  <div>
                    <h2 className="text-lg sm:text-xl font-semibold" style={{ color: 'rgb(18, 76, 114)' }}>Hold Request</h2>
                    <p className="text-xs sm:text-sm mt-0.5" style={{ color: 'rgba(18, 76, 114, 0.7)' }}>Place this request on hold temporarily</p>
                  </div>
                </div>
                <button onClick={() => { setIsHoldModalVisible(false); setNotes(""); setErrors({}); }} className="flex-shrink-0 p-1.5 rounded-lg" style={{ color: 'rgba(18, 76, 114, 0.5)', backgroundColor: 'rgba(18, 76, 114, 0.05)' }}>
                  <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
              </div>
              <div className="p-5 sm:p-6 space-y-5">
                <div className="flex items-start gap-3 p-4 rounded-xl" style={{ backgroundColor: 'rgba(245, 158, 11, 0.05)', border: '1px solid rgba(245, 158, 11, 0.15)' }}>
                  <svg className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: '#f59e0b' }} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                  <p className="text-sm text-gray-600">This will pause all activity on this request until it is removed from hold.</p>
                </div>
                <div className="grid grid-cols-2 gap-3 p-4 rounded-xl" style={{ backgroundColor: 'rgba(18, 76, 114, 0.03)', border: '1px solid rgba(18, 76, 114, 0.08)' }}>
                  <div><span className="text-xs font-medium block" style={{ color: 'rgba(18, 76, 114, 0.6)' }}>Request ID</span><span className="text-sm font-semibold" style={{ color: 'rgb(18, 76, 114)' }}>{selectedRequest?.referenceNumber || 'N/A'}</span></div>
                  <div><span className="text-xs font-medium block" style={{ color: 'rgba(18, 76, 114, 0.6)' }}>Department</span><span className="text-sm font-semibold" style={{ color: 'rgb(18, 76, 114)' }}>{requestDeptName || requestDepartment?.department_name || 'N/A'}</span></div>
                  <div><span className="text-xs font-medium block" style={{ color: 'rgba(18, 76, 114, 0.6)' }}>Hiring Manager</span><span className="text-sm font-semibold" style={{ color: 'rgb(18, 76, 114)' }}>{requestManagerName || 'N/A'}</span></div>
                  <div><span className="text-xs font-medium block" style={{ color: 'rgba(18, 76, 114, 0.6)' }}>Position</span><span className="text-sm font-semibold" style={{ color: 'rgb(18, 76, 114)' }}>{selectedRequestFromState?.position || 'N/A'}</span></div>
                </div>
                <div className="space-y-2">
                  <label className="block text-sm font-medium" style={{ color: 'rgb(18, 76, 114)' }}>Reason <span className="text-red-500">*</span></label>
                  <textarea className="w-full px-4 py-3.5 border rounded-xl outline-none transition text-gray-700 resize-vertical" rows="3" value={notes} onChange={(e) => { setNotes(e.target.value); if (errors.notes) setErrors({}); }} placeholder="Provide a reason for placing on hold..." style={{ borderColor: errors.notes ? '#ef4444' : 'rgba(245, 158, 11, 0.3)', backgroundColor: 'white', fontSize: '14px' }} />
                  {errors.notes && <div className="text-red-500 text-xs mt-1">{errors.notes}</div>}
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 p-5 sm:p-6 border-t rounded-b-2xl" style={{ borderColor: 'rgba(245, 158, 11, 0.1)', backgroundColor: 'rgba(245, 158, 11, 0.02)' }}>
                <button onClick={() => { setIsHoldModalVisible(false); setNotes(""); setErrors({}); }} className="flex-1 px-4 py-3 bg-white border rounded-xl font-medium" style={{ color: 'rgb(18, 76, 114)', borderColor: 'rgba(18, 76, 114, 0.2)' }}>Cancel</button>
                <button onClick={handleHold} disabled={isSubmitting || !notes.trim()} className="flex-1 px-4 py-3 text-white font-medium rounded-xl" style={{ backgroundColor: !notes.trim() || isSubmitting ? 'rgba(245, 158, 11, 0.5)' : '#f59e0b', cursor: !notes.trim() || isSubmitting ? 'not-allowed' : 'pointer' }}>
                  {isSubmitting ? 'Placing on Hold...' : 'Confirm Hold'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Unhold Modal */}
        {isUnHoldModalVisible && (
          <div className="fixed inset-0 backdrop-blur-sm flex justify-center items-center p-4 z-50" style={{ backgroundColor: 'rgba(18, 76, 114, 0.15)' }}>
            <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-full sm:max-w-lg md:max-w-xl lg:max-w-2xl mx-auto" style={{ borderColor: 'rgba(249, 115, 22, 0.2)', borderWidth: '1px', boxShadow: '0 20px 40px -15px rgba(249, 115, 22, 0.2)' }}>
              <div className="flex items-center justify-between p-5 sm:p-6 border-b" style={{ borderColor: 'rgba(249, 115, 22, 0.1)' }}>
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg" style={{ backgroundColor: 'rgba(249, 115, 22, 0.1)' }}>
                    <svg className="w-5 h-5" style={{ color: '#f97316' }} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" /></svg>
                  </div>
                  <div>
                    <h2 className="text-lg sm:text-xl font-semibold" style={{ color: 'rgb(18, 76, 114)' }}>Remove from Hold</h2>
                    <p className="text-xs sm:text-sm mt-0.5" style={{ color: 'rgba(18, 76, 114, 0.7)' }}>Resume this request from hold status</p>
                  </div>
                </div>
                <button onClick={() => { setIsUnHoldModalVisible(false); setNotes(""); setErrors({}); }} className="flex-shrink-0 p-1.5 rounded-lg" style={{ color: 'rgba(18, 76, 114, 0.5)', backgroundColor: 'rgba(18, 76, 114, 0.05)' }}>
                  <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
              </div>
              <div className="p-5 sm:p-6 space-y-5">
                <div className="flex items-start gap-3 p-4 rounded-xl" style={{ backgroundColor: 'rgba(249, 115, 22, 0.05)', border: '1px solid rgba(249, 115, 22, 0.15)' }}>
                  <svg className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: '#f97316' }} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                  <p className="text-sm text-gray-600">This will remove the hold and resume the request in its previous workflow state.</p>
                </div>
                <div className="grid grid-cols-2 gap-3 p-4 rounded-xl" style={{ backgroundColor: 'rgba(18, 76, 114, 0.03)', border: '1px solid rgba(18, 76, 114, 0.08)' }}>
                  <div><span className="text-xs font-medium block" style={{ color: 'rgba(18, 76, 114, 0.6)' }}>Request ID</span><span className="text-sm font-semibold" style={{ color: 'rgb(18, 76, 114)' }}>{selectedRequest?.referenceNumber || 'N/A'}</span></div>
                  <div><span className="text-xs font-medium block" style={{ color: 'rgba(18, 76, 114, 0.6)' }}>Department</span><span className="text-sm font-semibold" style={{ color: 'rgb(18, 76, 114)' }}>{requestDeptName || requestDepartment?.department_name || 'N/A'}</span></div>
                  <div><span className="text-xs font-medium block" style={{ color: 'rgba(18, 76, 114, 0.6)' }}>Hiring Manager</span><span className="text-sm font-semibold" style={{ color: 'rgb(18, 76, 114)' }}>{requestManagerName || 'N/A'}</span></div>
                  <div><span className="text-xs font-medium block" style={{ color: 'rgba(18, 76, 114, 0.6)' }}>Position</span><span className="text-sm font-semibold" style={{ color: 'rgb(18, 76, 114)' }}>{selectedRequestFromState?.position || 'N/A'}</span></div>
                </div>
                <div className="space-y-2">
                  <label className="block text-sm font-medium" style={{ color: 'rgb(18, 76, 114)' }}>Notes</label>
                  <textarea className="w-full px-4 py-3.5 border rounded-xl outline-none transition text-gray-700 resize-vertical" rows="3" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Add notes about removing hold..." style={{ borderColor: 'rgba(249, 115, 22, 0.3)', backgroundColor: 'white', fontSize: '14px' }} />
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 p-5 sm:p-6 border-t rounded-b-2xl" style={{ borderColor: 'rgba(249, 115, 22, 0.1)', backgroundColor: 'rgba(249, 115, 22, 0.02)' }}>
                <button onClick={() => { setIsUnHoldModalVisible(false); setNotes(""); setErrors({}); }} className="flex-1 px-4 py-3 bg-white border rounded-xl font-medium" style={{ color: 'rgb(18, 76, 114)', borderColor: 'rgba(18, 76, 114, 0.2)' }}>Cancel</button>
                <button onClick={handleUnHold} disabled={isSubmitting} className="flex-1 px-4 py-3 text-white font-medium rounded-xl" style={{ backgroundColor: isSubmitting ? 'rgba(249, 115, 22, 0.5)' : '#f97316', cursor: isSubmitting ? 'not-allowed' : 'pointer' }}>
                  {isSubmitting ? 'Removing Hold...' : 'Confirm Remove Hold'}
                </button>
              </div>
            </div>
          </div>
        )}

        {QuickAddCandidateModalWrapper()}
        {CandidatesModalWrapper()}
      </div>
    </div>
  );
}